// === COMMON STYLES FOR SLUITSTRAAT SCREENS ===
// Centralized styles to reduce duplication across screens
// Import this file in screens and use the shared styles
// All theme values are loaded from config.json via CSS variables

import React from 'react';

// === PRIMITIVES ===
// Shared bases composed into variants below, so common properties (shape, padding,
// typography, layout) live in one place and variants only declare what differs.
const buttonBase: React.CSSProperties = {
  padding: '12px 32px',
  border: 'none',
  borderRadius: 'var(--radius-pill, 50px)',
  fontSize: 'var(--saa-fs-base, 14px)',
  fontWeight: '500',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '8px',
};

// Single source for form-control dimensions: every text input AND dropdown derives
// from this base so both render pixel-identical side by side (height, padding,
// radius, typography). Change dimensions HERE only.
const formControlBase: React.CSSProperties = {
  width: '100%',
  padding: '12px 16px',
  fontSize: 'var(--saa-fs-md, 16px)',
  lineHeight: 'normal',
  border: '1px solid var(--saa-border, #e5e7eb)',
  borderRadius: 'var(--radius, 5px)',
  outline: 'none',
  boxSizing: 'border-box' as const,
  fontFamily: 'inherit',
  backgroundColor: '#fff',
  color: 'var(--saa-text-heading, #1f2937)',
};

// === DESIGN-LANGUAGE PRIMITIVES (extracted from step 1 for phase-3 reuse) ===
// The paper card: white surface, house radius, two-layer shadow, house padding.
// Compose with overrides ({...paperCard, ...}) instead of assembling cards from
// loose tokens — most phase-3 card work should be "use paperCard".
const paperCard: React.CSSProperties = {
  backgroundColor: '#ffffff',
  borderRadius: 'var(--radius-lg, 15px)',
  boxShadow: 'var(--saa-shadow-card)',
  padding: '24px',
};

// The in-content hairline (card title rule, nav divider) — always --saa-rule,
// never --saa-border (component edges).
const cardRule: React.CSSProperties = {
  borderTop: '1px solid var(--saa-rule, #d9dfe2)',
};

// Dropdown chrome layered on the base: hide the native widget, draw one chevron,
// reserve room for it. Never restate dimensions here.
const dropdownChrome: React.CSSProperties = {
  cursor: 'pointer',
  appearance: 'none' as const,
  WebkitAppearance: 'none' as const,
  MozAppearance: 'none' as const,
  backgroundImage: 'url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 24 24\' fill=\'none\' stroke=\'%239ca3af\' stroke-width=\'2\' stroke-linecap=\'round\' stroke-linejoin=\'round\'%3e%3cpolyline points=\'6 9 12 15 18 9\'%3e%3c/polyline%3e%3c/svg%3e")',
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'right 14px center',
  backgroundSize: '20px',
  paddingRight: '48px',
};

export const commonStyles: Record<string, React.CSSProperties> = {
  // ==================== PAGE LAYOUT ====================
  page: {
    flex: 1,
    backgroundColor: 'var(--background-dark, #F5F5F5)',
    padding: '40px 20px', // canonical page padding — sourced from the Aansprakelijkheid screen
  },
  container: {
    maxWidth: 'var(--container-max-width, 1080px)',
    margin: '0 auto',
    padding: '0 20px',
  },
  contentWrapper: {
    backgroundColor: '#FFFFFF',
    padding: '40px',
    borderRadius: 'var(--radius-lg, 15px)',
    boxShadow: 'var(--saa-shadow-card)',
  },
  containerNarrow: {
    maxWidth: '672px',
    margin: '0 auto',
  },
  // Canonical screen header — sourced from the Aansprakelijkheid screen (the reference
  // styling). All product screens share these so titles/subtitles stay consistent.
  contentHeader: {
    marginBottom: 'var(--saa-space-section, 40px)',
    textAlign: 'center' as const,
  },
  title: {
    fontSize: 'var(--saa-fs-display, 40px)',
    lineHeight: '1.15',
    fontWeight: '700',
    color: 'var(--saa-text-heading, #212426)',
    marginBottom: '1rem',
    letterSpacing: '-0.025em',
  },
  subtitle: {
    fontSize: 'var(--saa-fs-md, 16px)',
    color: 'var(--saa-text-muted, #6b7280)',
  },

  // ==================== FORM CONTAINERS ====================
  formContainer: {
    backgroundColor: '#fff',
    padding: '40px',
    borderRadius: 'var(--radius-lg, 15px)',
    boxShadow: 'var(--saa-shadow-card)',
    marginBottom: '30px',
    border: 'none',
  },
  sectionContainer: {
    backgroundColor: 'var(--card-bg, #fff)',
    padding: '32px',
    borderRadius: 'var(--radius-lg, 15px)',
    boxShadow: 'var(--saa-shadow-card)',
    marginBottom: '24px',
    border: 'none',
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '24px',
    paddingBottom: '16px',
    borderBottom: '2px solid var(--card-border, #e2e8f0)',
    flexWrap: 'wrap' as const,
  },
  sectionIconContainer: {
    width: '56px',
    height: '56px',
    borderRadius: 'var(--radius-lg, 15px)',
    backgroundColor: 'var(--surface-muted, #f7f9fc)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  sectionIcon: {
    width: '28px',
    height: '28px',
    opacity: 1,
  },
  sectionTitle: {
    fontSize: 'var(--saa-fs-xl, 20px)',
    fontWeight: '600',
    color: 'var(--saa-text-heading, #1f2937)',
    margin: '0',
  },

  // ==================== FORM FIELDS ====================
  fieldContainer: {
    marginBottom: '20px',
    flex: 1,
  },
  fieldLabel: {
    display: 'block',
    fontSize: 'var(--saa-fs-base, 14px)',
    fontWeight: '500',
    color: 'var(--saa-text-body, #374151)',
    marginBottom: '8px',
  },
  // Bolder, slightly larger label that titles a group of fields within a card.
  fieldGroupLabel: {
    display: 'block',
    fontSize: 'var(--saa-fs-base, 14px)',
    fontWeight: '600',
    color: 'var(--saa-text-heading, #1f2937)',
    marginBottom: '16px',
  },
  required: {
    color: 'var(--saa-danger, #d32f2f)',
  },
  fieldInput: {
    ...formControlBase,
    transition: 'border-color 0.2s',
  },
  fieldInputReadonly: {
    width: '100%',
    padding: '12px 16px',
    fontSize: 'var(--saa-fs-md, 16px)',
    border: '1px solid var(--saa-border, #e5e7eb)',
    borderRadius: 'var(--radius, 5px)',
    boxSizing: 'border-box' as const,
    backgroundColor: 'var(--saa-surface-muted, #f3f4f6)',
    color: 'var(--saa-text-body, #374151)',
    cursor: 'default',
  },
  fieldInputError: {
    border: '1px solid var(--saa-danger, #d32f2f)',
    backgroundColor: 'var(--saa-danger-bg, #fff5f5)',
  },
  fieldSelect: {
    ...formControlBase,
    ...dropdownChrome,
    transition: 'all 0.2s ease',
  },
  fieldSelectReadonly: {
    width: '100%',
    padding: '12px 16px',
    fontSize: 'var(--saa-fs-md, 16px)',
    border: '1px solid var(--saa-border, #e5e7eb)',
    borderRadius: 'var(--radius, 5px)',
    boxSizing: 'border-box' as const,
    backgroundColor: 'var(--saa-surface-muted, #f3f4f6)',
    color: 'var(--saa-text-body, #374151)',
    cursor: 'default',
  },
  fieldSelectError: {
    border: '1px solid var(--saa-danger, #d32f2f)',
    backgroundColor: 'var(--saa-danger-bg, #fff5f5)',
  },
  fieldHint: {
    fontSize: 'var(--saa-fs-xs, 12px)',
    color: 'var(--saa-border-strong, #d1d5db)',
    marginTop: '4px',
    marginBottom: '0',
  },
  errorMessage: {
    fontSize: 'var(--saa-fs-sm, 13px)',
    color: 'var(--saa-danger, #d32f2f)',
    marginTop: '6px',
    marginBottom: '0',
    fontWeight: '600',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },

  // ==================== CURRENCY INPUT ====================
  currencyInputContainer: {
    position: 'relative' as const,
    display: 'flex',
    alignItems: 'center',
  },
  currencySymbol: {
    position: 'absolute' as const,
    left: '16px',
    fontSize: 'var(--saa-fs-md, 16px)',
    color: 'var(--saa-text-muted, #6b7280)',
    fontWeight: '500',
    pointerEvents: 'none' as const,
    zIndex: 1,
  },
  currencyInput: {
    width: '100%',
    padding: '12px 16px 12px 36px',
    fontSize: 'var(--saa-fs-md, 16px)',
    border: '1px solid var(--saa-border, #e5e7eb)',
    borderRadius: 'var(--radius, 5px)',
    outline: 'none',
    transition: 'border-color 0.2s',
    boxSizing: 'border-box' as const,
    backgroundColor: '#fff',
    color: 'var(--saa-text-heading, #1f2937)',
  },

  // ==================== PHONE INPUT ====================
  phoneInputContainer: {
    position: 'relative' as const,
    display: 'flex',
    alignItems: 'center',
  },
  phoneIconContainer: {
    position: 'absolute' as const,
    left: '12px',
    top: '50%',
    transform: 'translateY(-50%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1,
    pointerEvents: 'none' as const,
  },
  phoneIcon: {
    width: '18px',
    height: '18px',
    color: 'var(--saa-text-faint, #9ca3af)',
  },
  phoneInput: {
    ...formControlBase,
    paddingLeft: '42px',
    transition: 'all 0.2s ease',
  },

  // ==================== BUTTONS ====================
  buttonPrimary: {
    ...buttonBase,
    backgroundColor: 'var(--primary-color, #056666)',
    color: '#fff',
    boxShadow: 'var(--saa-shadow-control)',
  },
  buttonSecondary: {
    ...buttonBase,
    backgroundColor: 'transparent',
    color: 'var(--saa-text-soft, #4b5563)',
  },
  buttonSecondaryHover: {
    backgroundColor: 'color-mix(in srgb, var(--primary-color, #056666) 8%, transparent)',
  },
  // Outline variant: secondary weight with the brand edge + label (low-frequency actions).
  buttonOutline: {
    ...buttonBase,
    backgroundColor: 'transparent',
    color: 'var(--primary-color, #056666)',
    border: '2px solid var(--primary-color, #056666)',
  },
  buttonDisabled: {
    opacity: 0.5,
    cursor: 'not-allowed',
    pointerEvents: 'none' as const,
  },
  buttonBusyWait: { opacity: 0.7, cursor: 'wait' as const },
  buttonBusyNotAllowed: { opacity: 0.7, cursor: 'not-allowed' as const },
  // In-button progress spinner (shared nav's nextLoading state)
  navSpinner: {
    width: '14px',
    height: '14px',
    border: '2px solid rgba(255,255,255,0.4)',
    borderTopColor: '#ffffff',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
    flexShrink: 0,
  },
  button: {
    marginTop: '20px',
    padding: '12px 24px',
    backgroundColor: 'var(--primary-color, #056666)',
    color: '#fff',
    border: 'none',
    borderRadius: 'var(--radius, 5px)',
    cursor: 'pointer',
  },

  // ==================== NAVIGATION ====================
  // Hairline above the Terug/Volgende bar — an in-content divider, so cardRule.
  navigation: {
    ...cardRule,
    display: 'flex',
    justifyContent: 'space-between',
    gap: '16px',
    marginTop: '32px',
    paddingTop: '20px',
  },

  // ==================== FORM FIELDS (shared, used across all screens) ====================
  formField: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '8px',
    marginBottom: '20px',
  },
  formLabel: {
    fontSize: 'var(--saa-fs-base, 14px)',
    fontWeight: 500,
    color: 'var(--saa-text-body, #374151)',
  },

  // ==================== COVERAGE INDICATOR (shared) ====================

  // ==================== COVERAGE OPTIONS CARD (shared) ====================
  // List card of coverage rows with a total badge (e.g. "Dekking opties").
  // Container reuses sectionContainer; header reuses sectionHeader + sectionIconContainer.
  coverageOptionsHeaderLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    flex: 1,
    minWidth: 0,
  },
  coverageOptionsTitle: {
    fontSize: 'var(--saa-fs-xl, 20px)',
    fontWeight: 600,
    color: 'var(--saa-text-heading, #1f2937)',
    margin: 0,
    whiteSpace: 'nowrap' as const,
    minWidth: 'fit-content',
    overflow: 'hidden' as const,
    textOverflow: 'ellipsis' as const,
  },
  coverageOptionsTotalBadge: {
    fontSize: 'var(--saa-fs-xl, 20px)',
    fontWeight: 700,
    color: 'var(--primary-color, #056666)',
    backgroundColor: 'color-mix(in srgb, var(--primary-color, #056666) 10%, white)',
    padding: '6px 14px',
    borderRadius: 'var(--radius-lg, 15px)',
    whiteSpace: 'nowrap' as const,
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    flexShrink: 0,
  },
  coverageOptionsRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px',
    backgroundColor: 'var(--surface-muted, #f7f9fc)',
    borderRadius: 'var(--radius-lg, 15px)',
    marginBottom: '8px',
    boxShadow: 'var(--saa-shadow-card)',
    border: 'none',
  },
  coverageOptionsRowLabel: {
    fontSize: 'var(--saa-fs-base, 14px)',
    fontWeight: 500,
    color: 'var(--saa-text-body, #374151)',
    flex: 1,
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '4px',
  },
  coverageOptionsRowSubtitle: {
    fontSize: 'var(--saa-fs-sm, 13px)',
    fontWeight: 500,
    color: 'var(--primary-color, #056666)',
  },
  coverageOptionsRowDescription: {
    fontSize: 'var(--saa-fs-xs, 12px)',
    color: 'var(--saa-text-muted, #6b7280)',
    lineHeight: '1.4',
  },
  coverageOptionsPriceBadge: {
    fontSize: 'var(--saa-fs-md, 16px)',
    fontWeight: 600,
    color: 'var(--primary-color, #056666)',
    backgroundColor: 'color-mix(in srgb, var(--primary-color, #056666) 10%, white)',
    padding: '4px 10px',
    borderRadius: 'var(--radius-lg, 15px)',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    whiteSpace: 'nowrap' as const,
  },
  // Smaller "+ €X" per-coverage pill (Rechtsbijstand); compact variant of coverageOptionsPriceBadge.
  coveragePriceBadgeSmall: {
    fontSize: 'var(--saa-fs-base, 14px)',
    fontWeight: '600',
    color: 'var(--primary-color, #056666)',
    backgroundColor: 'color-mix(in srgb, var(--primary-color, #056666) 10%, white)',
    padding: '4px 10px',
    borderRadius: 'var(--radius-lg, 15px)',
    whiteSpace: 'nowrap' as const,
  },

  // ==================== COVERAGE TOGGLE SCREEN (Rechtsbijstand; toggle switch shared with Inboedel/Personenauto) ====================
  coverageCard: {
    backgroundColor: '#fff',
    padding: '30px',
    borderRadius: 'var(--radius-lg, 15px)',
    boxShadow: 'var(--saa-shadow-card)',
    marginBottom: '20px',
    border: 'none',
  },
  coverageCardHeaderLeft: {
    display: 'flex',
    alignItems: 'center',
    flex: 1,
    minWidth: 0,
  },
  // sectionIconContainer + the 12px gap to the title (kept on the container so the
  // header markup carries no inline style overrides).
  coverageCardIconContainer: {
    width: '56px',
    height: '56px',
    borderRadius: 'var(--radius-lg, 15px)',
    backgroundColor: 'var(--surface-muted, #f7f9fc)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    marginRight: '12px',
  },
  coverageCardTitle: {
    fontSize: 'var(--saa-fs-xl, 20px)',
    fontWeight: '600',
    color: 'var(--saa-text-heading, #1f2937)',
    margin: 0,
    paddingLeft: '12px',
    overflow: 'hidden' as const,
    textOverflow: 'ellipsis' as const,
  },
  coverageToggleContent: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
  },
  // Greyed-out variant of coveragePriceBadgeSmall when its coverage is toggled off.
  coveragePriceBadgeDisabled: {
    backgroundColor: 'var(--saa-surface-muted, #f3f4f6)',
    color: 'var(--saa-text-muted, #6b7280)',
  },
  // On/off pill switch base (the <ToggleSwitch> component layers the dynamic
  // background colour / knob position on top of these).
  toggleSwitch: {
    position: 'relative' as const,
    width: '52px',
    height: '28px',
    borderRadius: 'var(--radius-lg, 15px)',
    cursor: 'pointer',
    transition: 'background-color 0.3s ease',
    flexShrink: 0,
    border: '1px solid var(--saa-border-strong, #d1d5db)',
  },
  toggleSlider: {
    position: 'absolute' as const,
    top: '50%',
    transform: 'translateY(-50%)',
    width: '24px',
    height: '24px',
    backgroundColor: '#fff',
    borderRadius: '50%',
    boxShadow: 'var(--saa-shadow-control)',
    transition: 'transform 0.3s ease',
  },
  toggleSwitchOn: { backgroundColor: 'var(--primary-color, #056666)' },
  toggleSwitchOff: { backgroundColor: 'var(--surface-muted, #f7f9fc)' },
  toggleSliderOn: { transform: 'translateY(-50%) translateX(24px)' },
  toggleSliderOff: { transform: 'translateY(-50%) translateX(2px)' },
  coverageDivider: {
    borderTop: '1px solid var(--saa-border-soft, #e0e0e0)',
    margin: '16px 0',
    width: '100%',
  },
  fieldRow: {
    display: 'flex',
    gap: '16px',
    flexWrap: 'wrap' as const,
  },
  fieldColumn: {
    flex: '1 1 140px',
    minWidth: '120px',
  },
  inlineErrorBanner: {
    color: 'var(--saa-danger, #d32f2f)',
    fontSize: 'var(--saa-fs-base, 14px)',
    fontWeight: 500,
    padding: '8px 12px',
    backgroundColor: 'var(--saa-danger-bg, #fff5f5)',
    borderRadius: 'var(--radius, 5px)',
    marginTop: '12px',
  },
  // Bottom navigation row — spans the page container so "Terug"/"Volgende" align
  // with the content edges (no own max-width; the container provides the width).
  screenNav: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '16px',
    marginTop: '32px',
  },
  productCounter: {
    fontSize: 'var(--saa-fs-base, 14px)',
    fontWeight: '500',
    color: 'var(--saa-text-muted, #6b7280)',
    textAlign: 'center' as const,
    padding: '8px 16px',
  },

  // ==================== PRODUCT INDICATOR (badges row, shared) ====================
  productIndicator: {
    display: 'flex',
    gap: '12px',
    marginBottom: '24px',
    flexWrap: 'wrap' as const,
    justifyContent: 'center',
  },
  productBadge: {
    padding: '6px 12px',
    borderRadius: 'var(--radius-lg, 15px)',
    fontSize: 'var(--saa-fs-2xs, 11px)',
    fontWeight: '500',
    backgroundColor: 'var(--saa-surface-muted, #f3f4f6)',
    color: 'var(--saa-text-body, #374151)',
    border: '1px solid var(--saa-border, #e5e7eb)',
    textTransform: 'uppercase' as const,
  },
  productBadgeActive: {
    backgroundColor: 'var(--primary-color, #056666)',
    color: '#ffffff',
    border: '1px solid var(--primary-color, #056666)',
    fontWeight: '600',
    textTransform: 'uppercase' as const,
  },
  productBadgeCompleted: {
    backgroundColor: 'var(--primary-light, #CBE0DE)',
    color: 'var(--completed-step-color, #056666)',
    border: '1px solid var(--primary-light, #CBE0DE)',
    fontWeight: '600',
    textTransform: 'uppercase' as const,
  },

  // ==================== ADDRESS INDICATORS ====================
  addressLookupLoading: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '16px',
    backgroundColor: 'var(--saa-surface-muted, #f3f4f6)',
    border: '2px solid var(--saa-border-strong, #d1d5db)',
    borderRadius: 'var(--radius, 5px)',
    marginTop: '16px',
    marginBottom: '16px',
  },
  addressLookupSpinner: {
    width: '20px',
    height: '20px',
    border: '2px solid var(--saa-border, #e5e7eb)',
    borderTop: '2px solid var(--primary-color, #056666)',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
    flexShrink: 0,
  },
  addressLookupText: {
    fontSize: 'var(--saa-fs-base, 14px)',
    color: 'var(--saa-text-soft, #4b5563)',
    fontWeight: '600',
  },
  addressValidatedIndicator: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '16px',
    backgroundColor: 'color-mix(in srgb, var(--primary-color, #056666) 10%, white)',
    border: '2px solid var(--primary-color, #056666)',
    borderRadius: 'var(--radius, 5px)',
    marginTop: '16px',
    marginBottom: '16px',
  },
  addressValidatedIcon: {
    width: '20px',
    height: '20px',
    color: 'var(--primary-color, #056666)',
    flexShrink: 0,
  },
  addressValidatedText: {
    fontSize: 'var(--saa-fs-base, 14px)',
    color: 'var(--primary-color, #056666)',
    fontWeight: '600',
  },
  addressErrorIndicator: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '16px',
    backgroundColor: 'var(--saa-danger-bg, #fff5f5)',
    border: '2px solid var(--saa-danger-strong, #dc2626)',
    borderRadius: 'var(--radius, 5px)',
    marginTop: '16px',
    marginBottom: '16px',
  },
  addressErrorIcon: {
    width: '20px',
    height: '20px',
    color: 'var(--saa-danger-strong, #dc2626)',
    flexShrink: 0,
  },
  addressErrorText: {
    fontSize: 'var(--saa-fs-base, 14px)',
    color: 'var(--saa-danger, #d32f2f)',
    fontWeight: '600',
  },

  additionSelectorContainer: {
    backgroundColor: 'var(--saa-notice-bg, #fffbeb)',
    border: '1px solid var(--saa-notice, #f59e0b)',
    borderRadius: 'var(--radius, 5px)',
    padding: '16px',
    marginBottom: '16px',
  },
  additionSelectorHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '12px',
  },
  additionSelectorIcon: {
    width: '20px',
    height: '20px',
    color: 'var(--saa-notice, #f59e0b)',
    flexShrink: 0,
  },
  additionSelectorText: {
    fontSize: 'var(--saa-fs-base, 14px)',
    color: 'var(--saa-notice-text, #92400e)',
    fontWeight: '500',
  },
  addressOptionsContainer: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '8px',
  },
  addressOption: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px 16px',
    backgroundColor: '#fff',
    border: '1px solid var(--saa-border, #e5e7eb)',
    borderRadius: 'var(--radius, 5px)',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },
  addressOptionRadio: {
    width: '20px',
    height: '20px',
    borderRadius: '50%',
    border: '2px solid var(--saa-border-strong, #d1d5db)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  addressOptionRadioInner: {
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    backgroundColor: 'transparent',
    transition: 'background-color 0.2s ease',
  },
  addressOptionLabel: {
    fontSize: 'var(--saa-fs-base, 14px)',
    color: 'var(--saa-text-body, #374151)',
  },
  fieldErrorMessage: {
    fontSize: 'var(--saa-fs-sm, 13px)',
    color: 'var(--saa-danger, #d32f2f)',
    marginTop: '6px',
    marginBottom: '0',
    fontWeight: '600',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },

  // ==================== LOCKED BANNERS ====================

  // ==================== ERROR/LOADING CONTAINERS ====================
  errorContainer: {
    maxWidth: '600px',
    margin: '100px auto',
    padding: '40px',
    backgroundColor: '#fff',
    borderRadius: 'var(--radius-lg, 15px)',
    boxShadow: 'var(--saa-shadow-card)',
    textAlign: 'center' as const,
  },
  errorTitle: {
    color: 'var(--saa-danger, #d32f2f)',
    marginBottom: '16px',
  },
  errorButton: {
    marginTop: '20px',
    padding: '12px 24px',
    backgroundColor: 'var(--primary-color, #056666)',
    color: '#fff',
    border: 'none',
    borderRadius: 'var(--radius, 5px)',
    cursor: 'pointer',
    fontSize: 'var(--saa-fs-md, 16px)',
    fontWeight: '500',
  },
  // Small inline spinner for a button in its busy state (white on brand button).
  buttonSpinner: {
    width: '16px',
    height: '16px',
    border: '2px solid #fff',
    borderTopColor: 'transparent',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
  },

  // ==================== MAIN LAYOUT ====================
  formColumn: {
    flex: '1 1 100%',
    minWidth: 0,
    maxWidth: '900px',
    margin: '0 auto',
  },

  // ==================== PRODUCT SELECTION ====================
  validationError: {
    padding: '12px 16px',
    backgroundColor: 'var(--saa-danger-bg, #fff5f5)',
    border: '1px solid var(--saa-danger-strong, #dc2626)',
    borderRadius: 'var(--radius, 5px)',
    color: 'var(--saa-danger-strong, #dc2626)',
    marginBottom: '24px',
    fontSize: 'var(--saa-fs-base, 14px)',
    textAlign: 'center' as const,
  },
  recommendedBadge: {
    position: 'absolute' as const,
    top: '-12px',
    left: '24px',
    zIndex: 10,
  },
  // Badge pill (shared base for the card badges): icon + uppercase micro-text.
  badgePill: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '4px 12px',
    borderRadius: 'var(--radius-pill, 50px)',
    fontSize: 'var(--saa-fs-2xs, 11px)',
    fontWeight: '700',
    letterSpacing: '.05em',
    textTransform: 'uppercase' as const,
    whiteSpace: 'nowrap' as const,
    lineHeight: '1.4',
  },
  badgeRecommended: {
    backgroundColor: 'var(--secondary-color, #F51C17)',
    color: '#fff',
  },
  badgeComingSoon: {
    backgroundColor: 'var(--primary-light, #CBE0DE)',
    color: 'var(--primary-600, #023131)',
  },
  productCard: {
    position: 'relative' as const,
    backgroundColor: '#ffffff',
    borderRadius: 'var(--radius-lg, 15px)',
    boxShadow: 'var(--saa-shadow-card)',
    cursor: 'pointer',
    transition: 'all 0.2s',
    border: 'none',
    height: '100%',
    display: 'flex',
    flexDirection: 'column' as const,
  },
  productCardHovered: {
    boxShadow: 'var(--saa-shadow-paper)',
  },
  productCardSelected: {
    border: '2px solid var(--primary-color, #056666)',
    backgroundColor: '#ffffff',
    boxShadow: 'var(--saa-shadow-card)',
    transform: 'none',
  },
  selectedBadge: {
    position: 'absolute' as const,
    top: '16px',
    right: '16px',
    width: '24px',
    height: '24px',
    borderRadius: '50%',
    backgroundColor: 'var(--primary-color, #056666)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  cardHeader: {
    padding: '24px 32px 0 24px',
    flex: 1,
  },
  iconContainer: {
    width: '56px',
    height: '56px',
    borderRadius: 'var(--radius-lg, 15px)',
    backgroundColor: 'var(--surface-muted, #f7f9fc)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: '16px',
    transition: 'all 0.2s',
  },
  productName: {
    fontSize: 'var(--saa-fs-lg, 18px)',
    fontWeight: '600',
    color: 'var(--foreground, #1a1a1a)',
    marginBottom: '8px',
  },
  productDescription: {
    fontSize: 'var(--saa-fs-base, 14px)',
    fontWeight: '400',
    color: 'var(--muted-foreground, #64748b)',
    marginBottom: '12px',
    lineHeight: '1.5',
    whiteSpace: 'nowrap' as const,
  },
  cardContent: {
    padding: '24px 32px 28px 24px',
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '20px',
  },
  productFeatures: {
    listStyle: 'none',
    padding: 0,
    margin: 0,
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '12px',
  },
  featureItem: {
    display: 'flex',
    alignItems: 'flex-start',
    fontSize: 'var(--saa-fs-base, 14px)',
  },
  featureCheck: {
    width: '16px',
    height: '16px',
    marginRight: '12px',
    flexShrink: 0,
    marginTop: '2px',
  },
  featureText: {
    color: 'var(--saa-text-muted, #6b7280)',
    fontWeight: '400',
    minWidth: 0,
    overflowWrap: 'break-word' as const,
  },
  bottomSection: {
    ...cardRule,
    paddingTop: '32px',
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '20px',
  },
  selectionSummary: {
    fontSize: 'var(--saa-fs-base, 14px)',
    fontWeight: '400',
    color: 'var(--saa-text-soft, #4b5563)',
    textAlign: 'center' as const,
  },
  navigationSingle: {
    display: 'flex',
    justifyContent: 'center',
    gap: '16px',
  },

  // ==================== SUMMARY PAGE ====================
  successBadge: {
    width: '80px',
    height: '80px',
    borderRadius: '50%',
    background: 'linear-gradient(135deg, var(--accent-color, #09B4B4) 0%, var(--primary-color, #056666) 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 24px',
    boxShadow: 'var(--saa-shadow-focus)',
  },
  successIcon: {
    width: '48px',
    height: '48px',
    color: '#fff',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: '1fr 400px',
    gap: '24px',
    alignItems: 'start',
  },
  leftColumn: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '24px',
  },
  rightColumn: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '24px',
  },
  infoGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '20px',
    marginBottom: '20px',
  },
  infoItem: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '4px',
  },
  infoLabel: {
    fontSize: 'var(--saa-fs-base, 14px)',
    fontWeight: '500',
    color: 'var(--saa-text-heading, #1f2937)',
  },
  infoValue: {
    fontSize: 'var(--saa-fs-base, 14px)',
    color: 'var(--saa-text-muted, #6b7280)',
    fontWeight: '400',
  },
  productInfo: {
    flex: 1,
  },
  premiumHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '16px',
  },
  premiumTitle: {
    fontSize: 'var(--saa-fs-base, 14px)',
    fontWeight: '500',
    color: 'var(--saa-text-muted, #6b7280)',
  },
  premiumAmountContainer: {
    textAlign: 'center' as const,
  },
  premiumAmountLarge: {
    fontSize: 'var(--saa-fs-2xl, 24px)',
    fontWeight: '700',
    color: 'var(--saa-text-heading, #1f2937)',
    lineHeight: '1.1',
    marginBottom: '4px',
  },
  premiumPeriodText: {
    fontSize: 'var(--saa-fs-base, 14px)',
    color: 'var(--saa-text-muted, #6b7280)',
    fontWeight: '500',
  },
  divider: {
    height: '1px',
    backgroundColor: 'var(--saa-border, #e5e7eb)',
    marginTop: '12px',
    marginBottom: '12px',
  },
  detailsList: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '16px',
  },
  detailItem: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '6px',
  },
  detailLabel: {
    fontSize: 'var(--saa-fs-base, 14px)',
    fontWeight: '500',
    color: 'var(--saa-text-heading, #1f2937)',
  },
  detailValue: {
    fontSize: 'var(--saa-fs-base, 14px)',
    color: 'var(--saa-text-muted, #6b7280)',
    fontWeight: '400',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  convertButtonIcon: {
    width: '20px',
    height: '20px',
  },

  // ==================== RADIO PILL GROUP ====================
  // Ja/Nee pill radios shared by the property screens (Woonhuis/Inboedel/Personenauto).
  // Base lives here; consumers pick selected/error variant by state. Backs <RadioGroup>.
  radioGroup: {
    display: 'flex',
    gap: '16px',
    marginTop: '4px',
  },
  radioOption: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    cursor: 'pointer',
    padding: '10px 20px',
    borderRadius: 'var(--radius, 5px)',
    border: '1px solid var(--saa-border, #e5e7eb)',
    backgroundColor: '#fff',
    transition: 'all 0.2s ease',
    fontSize: 'var(--saa-fs-base, 14px)',
    fontWeight: '500',
    color: 'var(--saa-text-heading, #1f2937)',
    userSelect: 'none' as const,
    minWidth: '80px',
    justifyContent: 'center',
  },
  radioOptionSelected: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    cursor: 'pointer',
    padding: '10px 20px',
    borderRadius: 'var(--radius, 5px)',
    border: '2px solid var(--primary-color, #056666)',
    backgroundColor: 'color-mix(in srgb, var(--primary-color, #056666) 6%, white)',
    transition: 'all 0.2s ease',
    fontSize: 'var(--saa-fs-base, 14px)',
    fontWeight: '600',
    color: 'var(--primary-color, #056666)',
    userSelect: 'none' as const,
    minWidth: '80px',
    justifyContent: 'center',
  },
  radioOptionError: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    cursor: 'pointer',
    padding: '10px 20px',
    borderRadius: 'var(--radius, 5px)',
    border: '1px solid var(--saa-danger, #d32f2f)',
    backgroundColor: 'var(--saa-danger-bg, #fff5f5)',
    transition: 'all 0.2s ease',
    fontSize: 'var(--saa-fs-base, 14px)',
    fontWeight: '500',
    color: 'var(--saa-text-heading, #1f2937)',
    userSelect: 'none' as const,
    minWidth: '80px',
    justifyContent: 'center',
  },
  radioDot: {
    width: '18px',
    height: '18px',
    borderRadius: '50%',
    border: '2px solid var(--saa-border-strong, #d1d5db)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    transition: 'all 0.2s ease',
  },
  radioDotSelected: {
    width: '18px',
    height: '18px',
    borderRadius: '50%',
    border: '2px solid var(--primary-color, #056666)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    transition: 'all 0.2s ease',
  },
  radioDotInner: {
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    backgroundColor: 'var(--primary-color, #056666)',
  },

  // ==================== AMBER NOTICE (monument / rented / *_INFO) ====================
  // Canonical amber warning callout. Semantic amber palette is intentionally fixed (not
  // brand-themeable). Used by Woonhuis/Inboedel monument+rented notices and reusable for
  // the AcceptanceQuestions/Confirmation *_INFO callouts (same var(--saa-notice-bg, #fffbeb)/var(--saa-notice, #f59e0b) palette).
  monumentWarning: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '16px',
    padding: '20px',
    backgroundColor: 'var(--saa-notice-bg, #fffbeb)',
    border: '2px solid var(--saa-notice, #f59e0b)',
    borderRadius: 'var(--radius, 5px)',
    marginTop: '16px',
    marginBottom: '16px',
  },
  monumentWarningIcon: {
    color: 'var(--saa-notice, #f59e0b)',
    flexShrink: 0,
    marginTop: '2px',
  },
  monumentWarningContent: {
    flex: 1,
  },
  monumentWarningTitle: {
    fontSize: 'var(--saa-fs-md, 16px)',
    fontWeight: '700',
    color: 'var(--saa-notice-text, #92400e)',
    marginBottom: '8px',
  },
  monumentWarningText: {
    fontSize: 'var(--saa-fs-base, 14px)',
    color: 'var(--saa-notice-text, #92400e)',
    lineHeight: '1.5',
    marginBottom: '12px',
  },
  monumentWarningContact: {
    fontSize: 'var(--saa-fs-base, 14px)',
    fontWeight: '600',
    color: 'var(--saa-notice-text, #92400e)',
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '4px',
  },
  monumentWarningLink: {
    color: 'var(--saa-notice-text, #92400e)',
    textDecoration: 'underline',
    fontWeight: '600',
    cursor: 'pointer',
  },

  // ==================== INFO POPUP ====================
  // (i) info tooltip for coverage/field explanations — backs <InfoTooltip>; popup
  // positioning comes from the per-screen *-info-popup classes in index.css.
  infoPopupHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '12px',
  },
  infoPopupTitle: {
    fontSize: 'var(--saa-fs-md, 16px)',
    fontWeight: '600' as const,
    color: 'var(--saa-text-heading, #1f2937)',
    margin: 0,
    minWidth: 0,
    overflowWrap: 'break-word' as const,
  },
  infoList: {
    listStyle: 'none',
    padding: 0,
    margin: 0,
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '8px',
  },
  infoListItem: {
    display: 'flex',
    gap: '8px',
    fontSize: 'var(--saa-fs-base, 14px)',
    color: 'var(--saa-text-body, #374151)',
    lineHeight: '1.5',
  },
  infoListIcon: {
    flexShrink: 0,
    marginTop: '2px',
  },
  // Reset for the (i) popup trigger, which is a real <button> (keyboard/touch accessible)
  // rendered as the bare inline icon.
  infoIconButton: {
    background: 'none',
    border: 'none',
    padding: 0,
    margin: 0,
    font: 'inherit',
    color: 'inherit',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
  },
  // Popup item text — wraps long words inside the fixed-width popup (was the
  // `.rechtsbijstand-info-popup li > span` rule before the list became phrasing content).
  infoPopupItemText: {
    fontWeight: 'normal' as const,
    minWidth: 0,
    flex: '1 1 auto',
    overflowWrap: 'anywhere' as const,
    wordBreak: 'break-word' as const,
  },

  // Shared all-risk coverage-card list/row styles (used by both the Woonhuis and
  // Inboedel "Dekking & premie" cards so they are guaranteed to match).
  iconNudge: { flexShrink: 0, marginTop: 2 },
  toggleRowStacked: { marginBottom: '16px', flexDirection: 'column' as const, alignItems: 'stretch', gap: '8px' },
  plainList: { listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column' as const, gap: '6px' },
  bulletItem: { display: 'flex', alignItems: 'flex-start', gap: '8px', fontSize: 'var(--saa-fs-sm, 13px)', lineHeight: 1.4, color: 'var(--saa-text-soft, #4b5563)' },
  baseCoverageBox: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'stretch',
    gap: '10px',
    padding: '20px',
    marginBottom: '16px',
    backgroundColor: 'color-mix(in srgb, var(--primary-color, #056666) 8%, white)',
    borderRadius: 'var(--radius-lg, 15px)',
    boxShadow: 'var(--saa-shadow-card)',
    border: 'none',
  },
  baseCoverageTitle: { fontSize: 'var(--saa-fs-lg, 18px)', fontWeight: '600', color: 'var(--saa-text-heading, #111827)', flex: 1, display: 'flex', alignItems: 'center', gap: '8px' },
  baseCoverageList: { listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column' as const, gap: '6px' },
  baseCoverageBullet: { display: 'flex', alignItems: 'flex-start', gap: '8px', fontSize: '15px', lineHeight: 1.4, color: 'var(--saa-text-body, #374151)' },
  // Price badge inside the box: the solid primary-light tint (the box itself stays lighter).
  baseCoverageBadge: {
    fontSize: 'var(--saa-fs-md, 16px)',
    fontWeight: '700',
    color: 'var(--primary-600, #023131)',
    backgroundColor: 'var(--primary-light, #CBE0DE)',
    padding: '6px 12px',
    borderRadius: 'var(--radius-lg, 15px)',
    whiteSpace: 'nowrap' as const,
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    flexShrink: 0,
  },

  // ==================== YES/NO OPTION BUTTON ====================
  // Ja/Nee answer buttons for the acceptance/confirmation screens. Distinct from the
  // radio pill set (filled selected state, wider padding, no dot).
  optionButton: {
    padding: '10px 32px',
    backgroundColor: '#fff',
    color: 'var(--saa-text-body, #374151)',
    borderWidth: '2px',
    borderStyle: 'solid' as const,
    borderColor: 'var(--saa-border, #e5e7eb)',
    borderRadius: 'var(--radius, 5px)',
    fontSize: 'var(--saa-fs-base, 14px)',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    textAlign: 'center' as const,
  },
  optionButtonSelected: {
    backgroundColor: 'var(--primary-color, #056666)',
    color: '#fff',
    borderColor: 'var(--primary-color, #056666)',
    fontWeight: '600',
  },
  optionButtonError: {
    borderColor: 'var(--saa-danger, #d32f2f)',
    backgroundColor: 'var(--saa-danger-bg, #fff5f5)',
  },

  // ==================== DOCUMENT DOWNLOAD LINK ====================
  // PDF download rows (terms / verzekeringskaart) on the summary + confirmation screens.
  documentsCard: {
    backgroundColor: '#ffffff',
    border: '1px solid var(--saa-border, #e5e7eb)',
    borderRadius: 'var(--radius-lg, 15px)',
    padding: '24px',
    marginBottom: '24px',
  },
  documentLink: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px 16px',
    backgroundColor: 'var(--saa-surface-subtle, #f9fafb)',
    border: '1px solid var(--saa-border, #e5e7eb)',
    borderRadius: 'var(--radius, 5px)',
    textDecoration: 'none',
    transition: 'all 0.2s ease',
    cursor: 'pointer',
  },
  documentIcon: {
    width: '24px',
    height: '24px',
    color: 'var(--primary-color, #056666)',
    flexShrink: 0,
  },
  documentLinkContent: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '2px',
  },
  documentLinkText: {
    fontSize: 'var(--saa-fs-base, 14px)',
    fontWeight: '500',
    color: 'var(--saa-text-heading, #1f2937)',
  },
  documentLinkSubtext: {
    fontSize: 'var(--saa-fs-sm, 13px)',
    color: 'var(--saa-text-muted, #6b7280)',
  },

  // ==================== ORDER-SUMMARY SIDEBAR ====================
  // 360px right rail (product list + monthly total + help/contact card) shared by the
  // application screens (Payment/Acceptance/Confirmation/WorkflowSummary). Backs the
  // <SummarySidebar> widget (built when those screens are migrated).
  summarySidebar: {
    width: 'var(--saa-sidebar-width, 320px)',
    flexShrink: 0,
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '20px',
  },
  summaryCard: {
    backgroundColor: '#fff',
    padding: '24px',
    borderRadius: 'var(--radius-lg, 15px)',
    boxShadow: 'var(--saa-shadow-card)',
    border: '1px solid var(--saa-border, #e5e7eb)',
  },
  summaryHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    marginBottom: '20px',
  },
  summaryHeaderIcon: {
    width: '20px',
    height: '20px',
    color: 'var(--primary-color, #056666)',
    flexShrink: 0,
  },
  summaryHeaderTitle: {
    fontSize: 'var(--saa-fs-md, 16px)',
    fontWeight: '600',
    color: 'var(--saa-text-heading, #1f2937)',
    margin: '0',
  },
  summaryProductList: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '12px',
  },
  summaryProductItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '8px 0',
  },
  summaryProductName: {
    fontSize: 'var(--saa-fs-base, 14px)',
    color: 'var(--saa-text-body, #374151)',
  },
  summaryProductPrice: {
    fontSize: 'var(--saa-fs-lg, 18px)',
    fontWeight: '600',
    color: 'var(--saa-text-heading, #1f2937)',
  },
  summaryDivider: {
    height: '1px',
    backgroundColor: 'var(--saa-border, #e5e7eb)',
    margin: '16px 0',
  },
  totalRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingTop: '8px',
  },
  totalLabel: {
    fontSize: 'var(--saa-fs-base, 14px)',
    fontWeight: '600',
    color: 'var(--saa-text-heading, #1f2937)',
  },
  totalAmount: {
    fontSize: 'var(--saa-fs-3xl, 32px)',
    fontWeight: '700',
    color: 'var(--primary-color, #056666)',
  },
  // Shared primitives exposed for screens: the paper card and its hairline.
  paperCard: paperCard,
  cardRule: cardRule,
  // Uppercase eyebrow + hairline running out to the right — the shared section
  // header pattern (used by the category headers; product screens follow in phase 3).
  eyebrowRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    marginBottom: '24px',
  },
  eyebrowRule: {
    flex: 1,
    height: '1px',
    backgroundColor: 'var(--saa-rule, #d9dfe2)',
  },
  // Sidebar cards: white paper on the grey page — the pale blue block left the
  // palette with the NU'91 restyle. Separation comes from the two-layer shadow.
  helpCard: {
    ...paperCard,
    overflow: 'hidden' as const,
  },
  helpTitle: {
    fontSize: 'var(--saa-fs-lg, 18px)',
    fontWeight: '700',
    color: 'var(--saa-text-heading, #212426)',
    margin: '0 0 12px 0',
  },
  helpDescription: {
    fontSize: 'var(--saa-fs-base, 14px)',
    color: 'var(--saa-text-muted, #6b7280)',
    marginBottom: '16px',
    lineHeight: '1.6',
  },
  contactInfo: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '12px',
  },
  contactItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  contactIcon: {
    width: '16px',
    height: '16px',
    color: 'var(--primary-color, #056666)',
    flexShrink: 0,
  },
  contactText: {
    fontSize: 'var(--saa-fs-sm, 13px)',
    color: 'var(--saa-text-body, #374151)',
  },
  contactLink: {
    fontSize: 'var(--saa-fs-sm, 13px)',
    color: 'var(--primary-color, #056666)',
    textDecoration: 'none',
    cursor: 'pointer',
    transition: 'color 0.2s ease',
  },
  // Uppercase eyebrow label — the shared pattern for section/sidebar headers.
  eyebrowLabel: {
    fontSize: 'var(--saa-fs-xs, 12px)',
    fontWeight: '700',
    letterSpacing: 'var(--saa-tracking-eyebrow, .14em)',
    textTransform: 'uppercase' as const,
    color: 'var(--saa-text-muted, #57666b)',
    margin: '0 0 8px 0',
  },
  // Advisor card extras (the trust card can take the same photo slot — both are
  // paperCard). The photo bleeds to the card edges through the 24px padding and
  // fades into the card at its bottom edge; the optional status slot overlays
  // that faded band so configured text stays legible on it.
  // No configured status text, no row — never placeholder data here.
  advisorPhotoWrap: {
    position: 'relative' as const,
    width: 'calc(100% + 48px)',
    margin: '-24px -24px 30px',
  },
  // Cover, position biased toward the face so the crop stays right at any
  // sidebar width; a brand photo with different proportions gets the same
  // face-high band. Asset ships at 2x the render width (640px WebP).
  // maxHeight wins over the 4:3 ratio at every sidebar width (a 4:3 box is
  // 240px+ tall), so the photo is a 208px band rather than a full portrait.
  advisorPhoto: {
    display: 'block',
    width: '100%',
    objectFit: 'cover' as const,
    objectPosition: '50% 25%',
    aspectRatio: '4 / 3',
    maxHeight: '208px',
  },
  advisorPhotoFade: {
    position: 'absolute' as const,
    left: 0,
    right: 0,
    bottom: 0,
    height: '45%',
    background: 'linear-gradient(180deg, rgba(255,255,255,0) 0%, #ffffff 100%)',
    pointerEvents: 'none' as const,
  },
  advisorStatusRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    margin: '12px 0 16px',
    fontSize: 'var(--saa-fs-sm, 13px)',
    color: 'var(--saa-text-muted, #57666b)',
  },
  advisorStatusDot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    backgroundColor: 'var(--success-color, #28A745)',
    flexShrink: 0,
  },
  // Trust card ("redenen om door te gaan")
  trustList: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '16px',
    margin: 0,
    padding: 0,
    listStyle: 'none',
  },
  trustItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
  },
  trustIconTile: {
    width: '36px',
    height: '36px',
    borderRadius: '50%',
    backgroundColor: 'var(--primary-light, #CBE0DE)',
    color: 'var(--primary-600, #023131)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  trustItemTitle: {
    fontSize: 'var(--saa-fs-base, 14px)',
    fontWeight: '700',
    color: 'var(--saa-text-heading, #212426)',
    margin: '0 0 2px 0',
  },
  trustItemText: {
    fontSize: 'var(--saa-fs-sm, 13px)',
    color: 'var(--saa-text-muted, #57666b)',
    lineHeight: '1.5',
    margin: 0,
  },
  subscriptionPrice: {
    ...cardRule,
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '2px',
    marginTop: '16px',
    paddingTop: '16px',
  },
  subscriptionPriceAmount: {
    fontSize: 'var(--saa-fs-md, 16px)',
    fontWeight: '700',
    color: 'var(--saa-text-heading, #212426)',
  },
  subscriptionPriceNote: {
    fontSize: 'var(--saa-fs-sm, 13px)',
    color: 'var(--saa-text-muted, #57666b)',
    lineHeight: '1.5',
  },
  // Selection card: primary-light tinted paper on the sidebar (steps 2+) with
  // its own ink pair (--saa-text-on-tint*); 28px vertical padding on purpose.
  selectionCard: {
    ...paperCard,
    padding: '28px 24px',
    backgroundColor: 'var(--primary-light, #CBE0DE)',
    color: 'var(--saa-text-on-tint-strong, #06322C)',
  },
  selectionCardEyebrow: {
    fontSize: 'var(--saa-fs-xs, 12px)',
    fontWeight: '700',
    letterSpacing: 'var(--saa-tracking-eyebrow-tight, .08em)',
    textTransform: 'uppercase' as const,
    color: 'var(--saa-text-on-tint, #0F5A52)',
    margin: '0 0 6px 0',
  },
  selectionCardTitle: {
    fontSize: 'var(--saa-fs-lg, 18px)',
    fontWeight: '700',
    color: 'var(--saa-text-on-tint-strong, #06322C)',
    margin: '0 0 14px 0',
  },
  selectionCardList: {
    listStyle: 'none',
    margin: 0,
    padding: 0,
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '10px',
  },
  // Row states on the tinted card: every row sits on a translucent white
  // surface; current = brighter + bold (takes over the chip row's "you are
  // here"); done = filled check disc; selected = plain check. No hover: rows
  // are NOT clickable.
  selectionCardItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '12px 14px',
    borderRadius: '8px',
    backgroundColor: 'rgba(255,255,255,0.6)',
    fontSize: 'var(--saa-fs-base, 14px)',
    fontWeight: '500',
    color: 'var(--saa-text-on-tint-strong, #06322C)',
  },
  selectionCardItemCurrent: {
    backgroundColor: 'rgba(255,255,255,0.85)',
    fontWeight: '700',
  },
  selectionCardItemIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    color: 'var(--saa-text-on-tint, #0F5A52)',
  },
  selectionCardCheckDone: {
    marginLeft: 'auto',
    flexShrink: 0,
    width: '18px',
    height: '18px',
    borderRadius: '50%',
    backgroundColor: 'var(--saa-text-on-tint, #0F5A52)',
    color: '#ffffff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  selectionCardCheck: {
    marginLeft: 'auto',
    flexShrink: 0,
    color: 'var(--saa-text-on-tint, #0F5A52)',
  },
  selectionCardFooter: {
    marginTop: '16px',
    paddingTop: '12px',
    borderTop: '1px solid color-mix(in srgb, var(--saa-text-on-tint-strong, #06322C) 15%, transparent)',
    fontSize: 'var(--saa-fs-sm, 13px)',
    color: 'var(--saa-text-on-tint, #0F5A52)',
  },
  // --- promoted shared utility keys (consolidation) ---
  addressContainer: {
    backgroundColor: '#fff',
    padding: '30px',
    borderRadius: 'var(--radius-lg, 15px)',
    boxShadow: 'var(--saa-shadow-card)',
    marginBottom: '20px',
    border: 'none',
  },
  addressField: {
    flex: 1,
    marginBottom: '16px',
  },
  buildingSpecsContainer: {
    backgroundColor: '#fff',
    padding: '30px',
    borderRadius: 'var(--radius-lg, 15px)',
    boxShadow: 'var(--saa-shadow-card)',
    marginBottom: '20px',
    border: 'none',
  },
  calculateButtonDisabled: {
    backgroundColor: 'var(--saa-text-faint, #9ca3af)',
    cursor: 'not-allowed',
    opacity: 0.7,
  },
  debugPanelContent: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '20px',
  },
  debugPanelHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px',
    paddingBottom: '12px',
    borderBottom: '1px solid var(--saa-border-soft, #e0e0e0)',
  },
  debugPanelTitle: {
    margin: '0',
    fontSize: 'var(--saa-fs-xl, 20px)',
    fontWeight: '600',
    color: 'var(--saa-text-heading, #1f2937)',
  },
  debugPre: {
    backgroundColor: '#1e1e1e',
    color: '#d4d4d4',
    padding: '16px',
    borderRadius: 'var(--radius, 5px)',
    overflow: 'auto',
    fontSize: 'var(--saa-fs-sm, 13px)',
    fontFamily: '"Consolas", "Monaco", "Courier New", monospace',
    margin: '0',
    maxHeight: '400px',
    lineHeight: '1.5',
  },
  debugSection: {
    backgroundColor: 'var(--saa-surface-subtle, #f9fafb)',
    padding: '16px',
    borderRadius: 'var(--radius, 5px)',
    border: '1px solid var(--saa-border-soft, #e0e0e0)',
  },
  debugSectionTitle: {
    margin: '0 0 12px 0',
    fontSize: 'var(--saa-fs-md, 16px)',
    fontWeight: '600',
    color: 'var(--saa-text-soft, #4b5563)',
  },
  flex1: { flex: 1 },
  iconOpaque: { opacity: 1 },
  infoButtonWrapper: {
    position: 'relative' as const,
    cursor: 'help',
    padding: '4px',
    display: 'flex',
    alignItems: 'center',
    borderRadius: '50%',
  },
  infoIcon: {
    display: 'block',
  },
  // Inline (i) icon that flows with the label text (Rechtsbijstand coverage rows). Mirrors the
  // Personenauto inline icon, but keeps position:relative so its info popup stays anchored to the
  // icon (top:100% in index.css), not to a far ancestor.
  infoIconInline: {
    position: 'relative' as const,
    cursor: 'help',
    padding: '4px',
    display: 'inline-flex',
    alignItems: 'center',
    borderRadius: '50%',
    verticalAlign: 'middle',
    marginLeft: '6px',
  },
  // Label text container that keeps a trailing inline (i) icon in the text flow — the icon belongs
  // to the label, trails the last word and wraps with it (Rechtsbijstand coverage rows).
  coverageLabelInline: {
    display: 'block',
    // break-word (not anywhere): long labels break only where needed/natural — and it does NOT
    // add a break opportunity right before the trailing inline (i) icon (which 'anywhere' did,
    // dropping the icon onto its own line).
    overflowWrap: 'break-word' as const,
    minWidth: 0,
  },
  // min-width:0 floor so a flex label column can shrink below its content and wrap (instead of
  // forcing the row wider than its card).
  minWidth0: { minWidth: 0 },
  // Fixed price+toggle group on the right of a coverage row — never shrinks, so a long wrapping
  // label can't push it out of the card.
  coverageRowEndGroup: { display: 'flex', alignItems: 'center', flexShrink: 0 },
  // Binds a label's last word + (i) icon so the icon never wraps alone onto its own line. Shared
  // by Rechtsbijstand + Personenauto coverage rows.
  labelTailNoWrap: { whiteSpace: 'nowrap' as const },
  mb16: { marginBottom: '16px' },
  mr12: { marginRight: '12px' },
  propertySpinner: { width: '16px', height: '16px', minWidth: '16px', minHeight: '16px', border: '2px solid color-mix(in srgb, var(--primary-color, #056666) 10%, white)', borderTop: '2px solid var(--primary-color, #056666)', borderRadius: '50%', animation: 'spin 1s linear infinite', flexShrink: 0 },
  radioGroupLocked: { opacity: 0.7, pointerEvents: 'none' as const },
  radioLocked: { opacity: 0.7, cursor: 'default' },
  rowBetweenFull: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' },
  rowCenter: { display: 'flex', alignItems: 'center' },
  rowCenter8: { display: 'flex', alignItems: 'center', gap: '8px' },
  rowCenterCenter8: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' },
  submitSpinner: { width: '16px', height: '16px', border: '2px solid #ffffff', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite' },
  toggleButton: {
    padding: '6px 12px',
    backgroundColor: 'var(--background-dark, #F5F5F5)',
    color: 'var(--saa-text-heading, #1f2937)',
    border: '1px solid var(--saa-border-strong, #d1d5db)',
    borderRadius: 'var(--radius, 5px)',
    fontSize: 'var(--saa-fs-base, 14px)',
    cursor: 'pointer',
    transition: 'all 0.2s',
    fontWeight: '500',
  },
  toggleLabel: {
    fontSize: 'var(--saa-fs-base, 14px)',
    fontWeight: '500',
    color: 'var(--saa-text-body, #374151)',
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  toggleRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px',
    backgroundColor: 'var(--surface-muted, #f7f9fc)',
    borderRadius: 'var(--radius-lg, 15px)',
    marginBottom: '8px',
    boxShadow: 'var(--saa-shadow-card)',
    border: 'none',
  },
  totalBadge: { fontSize: 'var(--saa-fs-xl, 20px)', fontWeight: '700', color: 'var(--primary-color, #056666)', backgroundColor: 'color-mix(in srgb, var(--primary-color, #056666) 10%, white)', padding: '6px 14px', borderRadius: 'var(--radius-lg, 15px)', whiteSpace: 'nowrap' as const, display: 'flex', alignItems: 'center', gap: '6px' },
  // Compact amber "Belangrijk" notice (Slotvragen pattern) — shared by AcceptanceQuestions and EndPage.
  importantNotice: {
    backgroundColor: 'rgba(254, 243, 199, 0.5)',
    border: '1px solid rgba(251, 191, 36, 0.3)',
    borderRadius: 'var(--radius, 5px)',
    padding: '14px 16px',
    marginTop: '20px',
  },
  noticeHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '6px',
  },
  noticeIcon: {
    width: '18px',
    height: '18px',
    color: 'var(--saa-notice, #f59e0b)',
    flexShrink: 0,
  },
  noticeTitle: {
    fontSize: 'var(--saa-fs-base, 14px)',
    fontWeight: '600',
    color: 'var(--saa-notice-text, #92400e)',
    margin: '0',
  },
  noticeText: {
    fontSize: 'var(--saa-fs-sm, 13px)',
    color: 'var(--saa-notice-text, #92400e)',
    margin: '0',
    lineHeight: '1.5',
    paddingLeft: '26px',
  },
};

// === SHARED COMPOSITES === (identical values single-sourced — change here, propagates to all referencing groups)
const propAddressTitle: React.CSSProperties = { fontSize: 'var(--saa-fs-xl, 20px)', fontWeight: '700', color: 'var(--saa-text-heading, #1f2937)', marginTop: '0', marginBottom: '0', };
const flowBackButton: React.CSSProperties = { padding: '12px 32px', background: 'transparent', color: 'var(--saa-text-soft, #4b5563)', border: 'none', borderRadius: 'var(--radius, 5px)', fontSize: 'var(--saa-fs-base, 14px)', fontWeight: '500', cursor: 'pointer', transition: 'all 0.2s ease', display: 'flex', alignItems: 'center', gap: '8px', };
const flowBackgroundDecoration: React.CSSProperties = { position: 'absolute' as const, top: '0', left: '50%', transform: 'translateX(-50%)', width: '100%', maxWidth: '1400px', height: '300px', pointerEvents: 'none' as const, zIndex: 0, };
const flowCard: React.CSSProperties = { backgroundColor: '#fff', padding: '24px', borderRadius: 'var(--radius-lg, 15px)', boxShadow: 'var(--saa-shadow-card)', border: '1px solid var(--saa-border, #e5e7eb)', marginBottom: '20px', };
const sectContainer: React.CSSProperties = { ...commonStyles.sectionContainer, padding: '30px', marginBottom: '20px' };
const infoFieldItem: React.CSSProperties = { ...commonStyles.infoItem, minWidth: 0, overflowWrap: 'break-word' as const };
const propToggleLabelCol: React.CSSProperties = { flex: 1, minWidth: 0 };
// Card-anchored (i) popup host: the toggle card is the positioned ancestor, so the
// .info-tooltip-popup-card popup hangs below the whole card (Personenauto + Inboedel).
const propToggleRowRelative: React.CSSProperties = { ...commonStyles.toggleRow, position: 'relative' };
const flowContainer: React.CSSProperties = { ...commonStyles.container, position: 'relative' as const, zIndex: 1, };
const flowContentWrapper: React.CSSProperties = { display: 'flex', gap: '24px', alignItems: 'flex-start', };
const propCoveragePremiumBadge: React.CSSProperties = { fontSize: 'var(--saa-fs-md, 16px)', fontWeight: '600', color: 'var(--primary-color, #056666)', backgroundColor: 'color-mix(in srgb, var(--primary-color, #056666) 10%, white)', padding: '4px 10px', borderRadius: 'var(--radius-lg, 15px)', whiteSpace: 'nowrap' as const, };
const propDebugPanel: React.CSSProperties = { backgroundColor: '#fff', padding: '20px', borderRadius: 'var(--radius-lg, 15px)', boxShadow: 'var(--saa-shadow-card)', border: 'none', margin: '0 0 30px', };
const sharedErrorBanner: React.CSSProperties = { backgroundColor: 'var(--saa-danger-strong, #dc2626)', color: '#fff', padding: '12px 20px', borderRadius: 'var(--radius, 5px)', marginBottom: '20px', fontSize: 'var(--saa-fs-base, 14px)', fontWeight: '500', textAlign: 'center' as const, };
const flowFeeLabel: React.CSSProperties = { fontSize: 'var(--saa-fs-xs, 12px)', color: 'var(--saa-text-muted, #6b7280)', };
const flowFeeList: React.CSSProperties = { display: 'flex', flexDirection: 'column' as const, gap: '4px', };
const flowFeeRow: React.CSSProperties = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', };
const sectHeader: React.CSSProperties = { ...commonStyles.sectionHeader, borderBottom: '2px solid var(--saa-border, #e5e7eb)' };
const dbgHeader: React.CSSProperties = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', paddingBottom: '12px', borderBottom: '1px solid var(--saa-border-soft, #e0e0e0)', };
const flowHeader: React.CSSProperties = { textAlign: 'center' as const, marginBottom: '32px', };
const flowMainContent: React.CSSProperties = { flex: '1', minWidth: '0', };
const sharedNavigation: React.CSSProperties = commonStyles.screenNav;
const flowPage: React.CSSProperties = { flex: 1, backgroundColor: 'var(--background-dark, #F5F5F5)', padding: '40px 20px', position: 'relative' as const, minHeight: '100vh', };
const dbgPre: React.CSSProperties = { backgroundColor: '#1e1e1e', color: '#d4d4d4', padding: '16px', borderRadius: 'var(--radius, 5px)', overflow: 'auto' as const, fontSize: 'var(--saa-fs-sm, 13px)', fontFamily: '"Consolas", "Monaco", "Courier New", monospace', margin: 0, maxHeight: '400px', lineHeight: 1.5, };
const flowProductItemLabel: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: '8px', };
const flowProductListDivider: React.CSSProperties = { height: '1px', backgroundColor: 'var(--saa-border, #e5e7eb)', margin: '8px 0', };
const dbgSection: React.CSSProperties = { backgroundColor: 'var(--saa-surface-subtle, #f9fafb)', padding: '16px', borderRadius: 'var(--radius, 5px)', border: '1px solid var(--saa-border-soft, #e0e0e0)', };
const flowSectionIcon: React.CSSProperties = { width: '28px', height: '28px', color: 'var(--primary-color, #056666)', flexShrink: 0, };
const flowSubtitle: React.CSSProperties = { fontSize: 'var(--saa-fs-md, 16px)', color: 'var(--saa-text-muted, #6b7280)', margin: 0, };
const flowTaxNote: React.CSSProperties = { textAlign: 'right' as const, fontSize: 'var(--saa-fs-2xs, 11px)', color: 'var(--saa-text-faint, #9ca3af)', padding: '4px 0 0', };
const sectTitle: React.CSSProperties = { ...commonStyles.sectionTitle, color: 'var(--saa-text-heading, #1f2937)', paddingLeft: '12px' };
const flowTitle: React.CSSProperties = { fontSize: 'var(--saa-fs-3xl, 32px)', fontWeight: '700', color: 'var(--saa-text-heading, #1f2937)', margin: '0 0 0.75rem 0', };
const dbgToggle: React.CSSProperties = { padding: '6px 12px', backgroundColor: 'var(--background-dark, #F5F5F5)', color: 'var(--saa-text-heading, #1f2937)', border: '1px solid var(--saa-border-strong, #d1d5db)', borderRadius: 'var(--radius, 5px)', fontSize: 'var(--saa-fs-base, 14px)', cursor: 'pointer', transition: 'all 0.2s', fontWeight: 500, };

// ==================== PER-SCREEN STYLE GROUPS ====================
// Single-use styles for one screen live here as a namespaced group (not in the shared
// `commonStyles` object, whose keys are reused across screens). The screen imports its
// group (`import { endPageStyles as styles } from '../styles/commonStyles'`) so no style
// definitions live in the page file. Values that diverge from a shared key (e.g. EndPage's
// own title/container sizing, success layout) belong here, not as overrides of canonical keys.

// END PAGE (success / review terminal screen)
// Vertical air between the stacked cards (and the action band) on the end page.
const endPageCardGap = '24px';

export const endPageStyles: Record<string, React.CSSProperties> = {
  page: {
    backgroundColor: 'var(--background-dark, #F5F5F5)',
    padding: '40px 20px 40px',
    position: 'relative',
    flex: 1,
    overflow: 'hidden',
    minHeight: '100vh',
  },
  container: flowContainer,
  successIcon: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: '24px',
  },
  checkmarkIcon: {
    width: '60px',
    height: '60px',
    color: 'var(--success-color, #22c55e)',
    stroke: 'var(--success-color, #22c55e)',
    strokeWidth: '3',
  },
  // Review-mode variant of checkmarkIcon — amber signals "needs review" (vs success green).
  checkmarkIconReview: {
    width: '60px',
    height: '60px',
    color: 'var(--saa-notice, #f59e0b)',
    stroke: 'var(--saa-notice, #f59e0b)',
    strokeWidth: '3',
  },
  title: {
    fontSize: 'var(--saa-fs-3xl, 32px)',
    fontWeight: '700',
    color: 'var(--saa-text-heading, #1f2937)',
    textAlign: 'center',
    marginBottom: '12px',
    marginTop: 0,
  },
  subtitle: {
    fontSize: 'var(--saa-fs-lg, 18px)',
    color: 'var(--saa-text-muted, #6b7280)',
    textAlign: 'center',
    marginBottom: '40px',
    marginTop: 0,
  },
  card: {
    backgroundColor: '#fff',
    borderRadius: 'var(--radius-lg, 15px)',
    padding: '30px',
    boxShadow: 'var(--saa-shadow-card)',
    marginBottom: endPageCardGap,
  },
  cardTitle: {
    fontSize: 'var(--saa-fs-xl, 20px)',
    fontWeight: '600',
    color: 'var(--saa-text-heading, #1f2937)',
    marginBottom: '24px',
    marginTop: 0,
    textAlign: 'center' as const,
  },
  stepsList: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '24px',
  },
  step: {
    display: 'flex',
    gap: '16px',
  },
  stepNumber: {
    width: '28px',
    height: '28px',
    borderRadius: '50%',
    backgroundColor: 'var(--primary-color, #056666)',
    color: '#ffffff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: '600',
    fontSize: 'var(--saa-fs-base, 14px)',
    flexShrink: 0,
  },
  // Review-mode variant of stepNumber — amber badge instead of brand teal.
  stepNumberReview: {
    width: '28px',
    height: '28px',
    borderRadius: '50%',
    backgroundColor: 'var(--saa-notice, #f59e0b)',
    color: '#ffffff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: '600',
    fontSize: 'var(--saa-fs-base, 14px)',
    flexShrink: 0,
  },
  stepContent: {
    flex: 1,
  },
  stepTitle: {
    fontSize: 'var(--saa-fs-md, 16px)',
    fontWeight: '600',
    color: 'var(--saa-text-heading, #1f2937)',
    marginBottom: '6px',
    marginTop: 0,
  },
  stepText: {
    fontSize: 'var(--saa-fs-base, 14px)',
    color: 'var(--saa-text-muted, #6b7280)',
    lineHeight: '1.6',
    margin: 0,
  },
  infoCard: {
    ...commonStyles.paperCard,
    marginBottom: '0',
  },
  infoTitle: {
    fontSize: 'var(--saa-fs-lg, 18px)',
    fontWeight: '600',
    color: 'var(--saa-text-heading, #1f2937)',
    marginBottom: '8px',
    marginTop: 0,
    textAlign: 'center' as const,
  },
  infoText: {
    fontSize: 'var(--saa-fs-base, 14px)',
    color: 'var(--saa-text-muted, #6b7280)',
    marginBottom: '16px',
    lineHeight: '1.6',
    textAlign: 'center' as const,
  },
  contactInfo: {
    display: 'flex',
    flexDirection: 'row' as const,
    gap: '24px',
    justifyContent: 'center',
    flexWrap: 'wrap' as const,
  },
  contactItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    fontSize: 'var(--saa-fs-base, 14px)',
    color: 'var(--saa-text-body, #374151)',
  },
  contactIcon: {
    width: '20px',
    height: '20px',
    color: 'var(--primary-color, #056666)',
  },
  contactLink: {
    color: 'var(--saa-text-body, #374151)',
    textDecoration: 'none',
    transition: 'color 0.2s',
  },
  confettiContainer: {
    position: 'fixed' as const,
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    pointerEvents: 'none',
    zIndex: 9999,
    overflow: 'hidden',
  },
  confetti: {
    position: 'absolute' as const,
    width: '12px',
    height: '12px',
    top: '-20px',
    opacity: 1,
    animation: 'confetti-fall linear forwards',
    borderRadius: 'var(--radius, 5px)',
  },
  // "Let op" provisional-cover exclusions box — reuses the shared amber Slotvragen notice
  // (single-sourced from commonStyles), with a little bottom spacing to sit between cards.
  importantNotice: { ...commonStyles.importantNotice, marginBottom: endPageCardGap },
  noticeHeader: commonStyles.noticeHeader,
  noticeIcon: commonStyles.noticeIcon,
  noticeTitle: commonStyles.noticeTitle,
  noticeText: commonStyles.noticeText,
  noticeList: {
    margin: '4px 0 12px',
    paddingLeft: '46px',
    color: 'var(--saa-notice-text, #92400e)',
    fontSize: 'var(--saa-fs-sm, 13px)',
    lineHeight: '1.5',
  },
  noticeListItem: { marginBottom: '4px' },
  newApplicationActions: {
    display: 'flex',
    justifyContent: 'center',
    margin: `${endPageCardGap} 0`,
  },
  newApplicationButton: commonStyles.buttonOutline,
  noticeSubList: {
    margin: '4px 0 0',
    paddingLeft: '20px',
    listStyleType: 'circle' as const,
  },
};

// CLIENT INFORMATION (personal-info form). Spreads the shared object + this screen's
// unique header keys, so the screen imports one group and keeps zero styles on the page.
export const clientInformationStyles: Record<string, React.CSSProperties> = {
  ...commonStyles,
  // Same container width as product selection (commonStyles.container via the spread) —
  // the old containerNarrow (672px) left the form ~256px next to the help sidebar.
  // Icon + stacked title/subtitle header (unique: sectionHeader centers a single line)
  formHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '16px',
    marginBottom: '32px',
    paddingBottom: '24px',
    borderBottom: '1px solid var(--saa-surface-muted, #f3f4f6)',
  },
  userIconContainer: {
    width: '56px',
    height: '56px',
    borderRadius: 'var(--radius-lg, 15px)',
    backgroundColor: 'var(--surface-muted, #f7f9fc)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  formTitle: {
    fontSize: 'var(--saa-fs-xl, 20px)',
    fontWeight: '600',
    color: 'var(--saa-text-heading, #1f2937)',
    margin: '0 0 4px 0',
  },
  formSubtitle: {
    fontSize: 'var(--saa-fs-base, 14px)',
    color: 'var(--saa-text-muted, #6b7280)',
    margin: 0,
  },
};

// PRODUCT SELECTION (the product picker). Spreads the shared object + this screen's
// overrides and unique card/category/popup keys. Pure pass-through keys (contentHeader,
// title, badges, feature*, etc.) and the info-popup sub-styles are NOT repeated here —
// they resolve through the spread from the shared object.
export const productSelectionStyles: Record<string, React.CSSProperties> = {
  ...commonStyles,
  page: {
    ...commonStyles.page,
    padding: '8px 16px 40px',
    minHeight: '76vh',
    overflow: 'visible' as const,
    paddingBottom: '120px',
  },
  container: {
    ...commonStyles.container,
    margin: '4em auto',
  },
  subtitle: {
    ...commonStyles.subtitle,
    fontSize: 'var(--saa-fs-lg, 18px)',
    maxWidth: '672px',
    margin: '0 auto',
    lineHeight: '1.5',
  },
  // Category layout
  categorySection: {
    marginBottom: '32px',
  },
  // Category header = the shared eyebrow-header pattern (label from eyebrowLabel,
  // row + rule from the extracted primitives).
  categoryHeader: commonStyles.eyebrowRow,
  categoryLabel: {
    fontSize: 'var(--saa-fs-xs, 12px)',
    fontWeight: '700' as const,
    color: 'var(--saa-text-muted, #57666b)',
    letterSpacing: 'var(--saa-tracking-eyebrow, .14em)',
    textTransform: 'uppercase' as const,
    whiteSpace: 'nowrap' as const,
  },
  categoryLine: commonStyles.eyebrowRule,
  categoryGrid: {
    display: 'grid',
    gap: '24px',
    // Fluid: as many ~280px+ cards as the available width fits — no manual column
    // breakpoints. auto-fill (not auto-fit) so a partial last row keeps normal-width
    // cards instead of stretching to fill the row.
    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
  },
  // Coming-soon badge anchor (the pill itself is the shared badgePill/badgeComingSoon)
  comingSoonBadge: {
    position: 'absolute' as const,
    top: '-12px',
    left: '24px',
    zIndex: 1,
  },
  // Paper card: 2px transparent border reserves the selected-state border slot so
  // selection never shifts layout; separation comes from the two-layer shadow.
  productCard: {
    ...commonStyles.productCard,
    border: '2px solid transparent',
    borderRadius: 'var(--radius-lg, 15px)',
    boxShadow: 'var(--saa-shadow-card)',
  },
  productCardComingSoon: {
    border: '2px solid var(--saa-surface-muted, #f3f4f6)',
    cursor: 'default',
    backgroundColor: 'var(--saa-surface-subtle, #f9fafb)',
    boxShadow: 'none',
  },
  // Unselected selection circle (pairs with canonical selectedBadge)
  unselectedBadge: {
    position: 'absolute' as const,
    top: '16px',
    right: '16px',
    width: '24px',
    height: '24px',
    borderRadius: '50%',
    border: '2px solid var(--saa-border, #dce3e5)',
    backgroundColor: '#ffffff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s ease',
  },
  emptyCircle: {
    width: '16px',
    height: '16px',
    borderRadius: '50%',
    backgroundColor: 'transparent',
  },
  cardHeader: {
    ...commonStyles.cardHeader,
    padding: '24px 28px 0',
    flex: '0 0 auto' as const,
  },
  productName: {
    ...commonStyles.productName,
    marginBottom: '0',
    lineHeight: '1.2',
    // Title is a flex child (productNameRow): without minWidth 0 long single words
    // ("Aansprakelijkheid", "Bromfiets/scooter") overflow instead of wrapping.
    minWidth: 0,
    overflowWrap: 'break-word' as const,
  },
  // minHeight = exactly two lines (14px * 1.5): this is the row-alignment
  // mechanism — it reserves the second line on one-line descriptions so the
  // hairline lands at the same y across a row. Remove it and rows misalign.
  productDescription: {
    ...commonStyles.productDescription,
    whiteSpace: 'normal' as const,
    minHeight: '42px',
    marginBottom: '8px',
  },
  // Hairline between the title block and the feature list, inset to the card padding.
  cardContent: {
    ...commonStyles.cardContent,
    ...commonStyles.cardRule,
    margin: '8px 28px 0',
    padding: '12px 0 20px',
  },
  selectionSummary: {
    ...commonStyles.selectionSummary,
    fontWeight: '400',
  },
  buttonPrimary: {
    ...commonStyles.buttonPrimary,
    padding: '16px 48px',
    minWidth: '200px',
    width: 'auto',
    justifyContent: 'center',
  },
  productNameRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: '4px',
    marginBottom: '8px',
  },
  // Former inline styles lifted out of the ProductCard / mobile-category JSX
  cardOuter: {
    position: 'relative' as const,
    display: 'flex',
    flexDirection: 'column' as const,
    height: '100%',
  },
  selectedCheck: {
    width: '16px',
    height: '16px',
    color: 'white',
  },
  iconComingSoon: {
    opacity: 0.5,
  },
  comingSoonText: {
    color: 'var(--saa-text-faint, #9ca3af)',
  },
  // Feature check icon — selection no longer recolors the list (the 2px border +
  // filled check carry the selected state), so only coming-soon/default remain.
  featureCheckComingSoon: {
    ...commonStyles.featureCheck,
    color: 'var(--saa-border-strong, #d1d9dc)',
    strokeWidth: 2,
  },
  featureCheckDefault: {
    ...commonStyles.featureCheck,
    color: 'var(--primary-color, #056666)',
    strokeWidth: 2.5,
  },
  featureTextComingSoon: {
    ...commonStyles.featureText,
    color: 'var(--saa-text-faint, #9ca3af)',
  },
  featureTextDefault: {
    ...commonStyles.featureText,
    fontWeight: '500',
  },
  mobileCategory: {
    marginBottom: '12px',
  },
  mobileCategoryToggleInner: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '8px',
    flex: 1,
    minWidth: 0,
  },
  mobileCategoryToggleRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
};

// ACCEPTANCE QUESTIONS (application flow). The sidebar/summary, option-button, totals,
// help and contact keys are ALIASES of the shared keys this screen seeded in Item 0 (dedup);
// the rest are screen-local. No `...commonStyles` spread — the screen only used its own keys.
export const acceptanceQuestionsStyles: Record<string, React.CSSProperties> = {
  // --- reused shared keys (originally sourced from this screen) ---
  optionButton: commonStyles.optionButton,
  optionButtonSelected: commonStyles.optionButtonSelected,
  optionButtonError: commonStyles.optionButtonError,
  sidebar: commonStyles.summarySidebar,
  sidebarHeader: commonStyles.summaryHeader,
  sidebarIcon: commonStyles.summaryHeaderIcon,
  sidebarTitle: commonStyles.summaryHeaderTitle,
  summaryCard: commonStyles.summaryCard,
  productList: commonStyles.summaryProductList,
  productItem: commonStyles.summaryProductItem,
  productName: commonStyles.summaryProductName,
  productPrice: commonStyles.summaryProductPrice,
  divider: commonStyles.summaryDivider,
  totalRow: commonStyles.totalRow,
  totalLabel: commonStyles.totalLabel,
  totalAmount: commonStyles.totalAmount,
  helpCard: commonStyles.helpCard,
  helpTitle: commonStyles.helpTitle,
  helpDescription: commonStyles.helpDescription,
  contactInfo: commonStyles.contactInfo,
  contactItem: commonStyles.contactItem,
  contactIcon: commonStyles.contactIcon,
  contactText: commonStyles.contactText,
  contactLink: commonStyles.contactLink,
  // --- screen-local keys ---
  page: flowPage,
  backgroundDecoration: flowBackgroundDecoration,
  container: flowContainer,
  contentWrapper: flowContentWrapper,
  mainContent: flowMainContent,
  header: flowHeader,
  title: flowTitle,
  subtitle: flowSubtitle,
  card: flowCard,
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    marginBottom: '12px',
  },
  sectionIcon: flowSectionIcon,
  sectionTitle: commonStyles.sectionTitle,
  sectionDescription: {
    fontSize: 'var(--saa-fs-base, 14px)',
    color: 'var(--saa-text-muted, #6b7280)',
    marginBottom: '20px',
    lineHeight: '1.5',
  },
  questionSection: {
    marginBottom: '20px',
  },
  questionTitle: {
    fontSize: 'var(--saa-fs-md, 16px)',
    fontWeight: '600',
    color: 'var(--saa-text-heading, #1f2937)',
    marginBottom: '12px',
  },
  required: commonStyles.required,
  buttonGroup: {
    display: 'flex',
    gap: '12px',
  },
  buttonGroupError: {},
  errorMessage: {
    color: 'var(--saa-danger, #d32f2f)',
    fontSize: 'var(--saa-fs-sm, 13px)',
    fontWeight: '600',
    marginTop: '8px',
    marginBottom: '0',
  },
  textareaError: {
    borderColor: 'var(--saa-danger, #d32f2f)',
    backgroundColor: 'var(--saa-danger-bg, #fff5f5)',
  },
  importantNotice: commonStyles.importantNotice,
  noticeHeader: commonStyles.noticeHeader,
  noticeIcon: commonStyles.noticeIcon,
  noticeTitle: commonStyles.noticeTitle,
  noticeText: commonStyles.noticeText,
  footer: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: '16px',
    marginTop: '32px',
    width: '100%',
  },
  backButton: flowBackButton,
  continueButton: {
    padding: '14px 36px',
    background: 'linear-gradient(135deg, var(--primary-color, #056666) 0%, var(--primary-color, #056666) 100%)',
    color: '#fff',
    border: 'none',
    borderRadius: 'var(--radius, 5px)',
    fontSize: 'var(--saa-fs-md, 16px)',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    boxShadow: 'var(--saa-shadow-focus)',
  },
  explanationContainer: {
    marginTop: '16px',
    padding: '16px',
    backgroundColor: 'var(--background-dark, #F5F5F5)',
    borderRadius: 'var(--radius, 5px)',
    border: '1px solid var(--saa-border, #e5e7eb)',
  },
  explanationLabel: {
    display: 'block',
    fontSize: 'var(--saa-fs-base, 14px)',
    fontWeight: '600',
    color: 'var(--saa-text-body, #374151)',
    marginBottom: '8px',
  },
  explanationTextarea: {
    width: '100%',
    padding: '12px',
    fontSize: 'var(--saa-fs-base, 14px)',
    color: 'var(--saa-text-heading, #1f2937)',
    backgroundColor: '#fff',
    border: '2px solid var(--saa-border, #e5e7eb)',
    borderRadius: 'var(--radius, 5px)',
    fontFamily: 'inherit',
    resize: 'vertical' as const,
    transition: 'all 0.2s ease',
    outline: 'none',
  },
  // --- former inline styles lifted out of the JSX ---
  requiredNoWrap: {
    color: 'var(--saa-danger, #d32f2f)',
    whiteSpace: 'nowrap' as const,
  },
  // Amber *_INFO answer callout (shown on "Ja" for QUESTIONS_WITH_INFO)
  answerInfoCallout: {
    marginTop: '12px',
    padding: '14px 16px',
    backgroundColor: 'var(--saa-notice-bg, #fffbeb)',
    border: '1px solid var(--saa-notice, #f59e0b)',
    borderRadius: 'var(--radius, 5px)',
    display: 'flex',
    alignItems: 'flex-start',
    gap: '10px',
  },
  answerInfoIcon: {
    minWidth: '20px',
    width: '20px',
    height: '20px',
    color: 'var(--saa-notice, #f59e0b)',
    marginTop: '1px',
  },
  answerInfoText: {
    margin: 0,
    fontSize: 'var(--saa-fs-sm, 13px)',
    lineHeight: '1.6',
    color: 'var(--saa-notice-text, #92400e)',
    whiteSpace: 'pre-line' as const,
  },
  // Sidebar product list + fee rows
  productItemLabel: flowProductItemLabel,
  productListDivider: flowProductListDivider,
  feeList: flowFeeList,
  feeRow: flowFeeRow,
  feeLabel: flowFeeLabel,
  taxNote: flowTaxNote,
};

// WORKFLOW DEBUG PANEL (shared dev component). Component styles live here too — no CSS is
// defined inside component files.
export const workflowDebugPanelStyles: Record<string, React.CSSProperties> = {
  // Full container width — sits below the bottom navigation, aligned with the
  // content above (no own max-width cap).
  panel: {
    backgroundColor: '#fff',
    padding: '20px',
    borderRadius: 'var(--radius-lg, 15px)',
    boxShadow: 'var(--saa-shadow-card)',
    border: 'none',
    margin: '30px 0',
  },
  header: dbgHeader,
  title: {
    margin: 0,
    fontSize: 'var(--saa-fs-xl, 20px)',
    fontWeight: 600,
    color: 'var(--saa-text-heading, #1f2937)',
  },
  toggle: dbgToggle,
  content: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '20px',
  },
  section: dbgSection,
  sectionTitle: {
    margin: '0 0 12px 0',
    fontSize: 'var(--saa-fs-md, 16px)',
    fontWeight: 600,
    color: 'var(--saa-text-soft, #4b5563)',
  },
  devSectionTitle: {
    margin: '0 0 12px 0',
    fontSize: 'var(--saa-fs-md, 16px)',
    fontWeight: 600,
    color: 'var(--primary-color, #056666)',
  },
  pre: dbgPre,
};

// CONFIRMATION (application flow). Sidebar/summary, totals, help and contact keys ALIAS the
// shared keys this flow seeded in Item 0 (dedup); the rest are screen-local (consent checkbox
// cluster, info display, confirm/back buttons). No `...commonStyles` spread.
export const confirmationStyles: Record<string, React.CSSProperties> = {
  // --- reused shared keys ---
  sidebar: commonStyles.summarySidebar,
  summaryCard: commonStyles.summaryCard,
  sidebarHeader: commonStyles.summaryHeader,
  sidebarIcon: commonStyles.summaryHeaderIcon,
  sidebarTitle: commonStyles.summaryHeaderTitle,
  productList: commonStyles.summaryProductList,
  productItem: commonStyles.summaryProductItem,
  productName: commonStyles.summaryProductName,
  productPrice: commonStyles.summaryProductPrice,
  divider: commonStyles.summaryDivider,
  totalRow: commonStyles.totalRow,
  totalLabel: commonStyles.totalLabel,
  totalAmount: commonStyles.totalAmount,
  helpCard: commonStyles.helpCard,
  helpTitle: commonStyles.helpTitle,
  helpDescription: commonStyles.helpDescription,
  contactInfo: commonStyles.contactInfo,
  contactItem: commonStyles.contactItem,
  contactIcon: commonStyles.contactIcon,
  contactText: commonStyles.contactText,
  contactLink: commonStyles.contactLink,
  required: commonStyles.required,
  // --- screen-local keys ---
  page: flowPage,
  backgroundDecoration: flowBackgroundDecoration,
  container: flowContainer,
  contentWrapper: flowContentWrapper,
  mainContent: flowMainContent,
  header: flowHeader,
  title: flowTitle,
  subtitle: flowSubtitle,
  errorBanner: sharedErrorBanner,
  card: flowCard,
  footer: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: '16px',
    marginTop: '32px',
    gridColumn: '1 / -1',
    width: '100%',
  },
  backButton: flowBackButton,
  confirmButton: {
    padding: '14px 36px',
    background: 'linear-gradient(135deg, var(--primary-color, #056666) 0%, var(--primary-color, #056666) 100%)',
    color: '#fff',
    border: 'none',
    borderRadius: 'var(--radius, 5px)',
    fontSize: 'var(--saa-fs-md, 16px)',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    boxShadow: 'var(--saa-shadow-focus)',
  },
  confirmButtonDisabled: {
    opacity: 0.6,
    cursor: 'not-allowed',
    pointerEvents: 'none' as const,
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '16px',
  },
  sectionIcon: flowSectionIcon,
  sectionTitle: {
    fontSize: 'var(--saa-fs-xl, 20px)',
    fontWeight: '700',
    color: 'var(--saa-text-heading, #1f2937)',
    margin: '0',
  },
  checkboxContainer: {
    display: 'flex',
    alignItems: 'flex-start',
    marginBottom: '12px',
  },
  checkbox: {
    marginTop: '2px',
    marginRight: '10px',
    minWidth: '18px',
    width: '18px',
    height: '18px',
    cursor: 'pointer',
    flexShrink: 0,
    WebkitAppearance: 'none' as const,
    MozAppearance: 'none' as const,
    appearance: 'none' as const,
    backgroundColor: '#fff',
    borderWidth: '2px',
    borderStyle: 'solid',
    borderColor: 'var(--saa-border-strong, #d1d5db)',
    borderRadius: 'var(--radius, 5px)',
    position: 'relative' as const,
    backgroundImage: 'none',
    backgroundSize: 'auto',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    outline: '2px solid transparent',
    outlineOffset: '2px',
    transition: 'outline-color 0.15s ease-in-out, border-color 0.15s ease-in-out',
  },
  checkboxChecked: {
    backgroundColor: 'var(--primary-color, #056666)',
    borderColor: 'var(--primary-color, #056666)',
    backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 24 24\' fill=\'none\' stroke=\'white\' stroke-width=\'3\' stroke-linecap=\'round\' stroke-linejoin=\'round\'%3E%3Cpolyline points=\'20 6 9 17 4 12\'%3E%3C/polyline%3E%3C/svg%3E")',
    backgroundSize: '14px 14px',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
  },
  checkboxLabelContainer: {
    flex: 1,
  },
  checkboxLabel: {
    fontSize: 'var(--saa-fs-base, 14px)',
    color: 'var(--saa-text-heading, #1f2937)',
    lineHeight: '1.5',
    cursor: 'pointer',
    fontWeight: '600',
    display: 'block',
    marginBottom: '4px',
  },
  checkboxHelpText: {
    fontSize: 'var(--saa-fs-base, 14px)',
    color: 'var(--saa-text-muted, #6b7280)',
    lineHeight: '1.6',
    margin: 0,
  },
  errorMessage: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginTop: '12px',
    padding: '12px',
    backgroundColor: 'var(--saa-danger-bg, #fff5f5)',
    border: '1px solid var(--saa-danger-strong, #dc2626)',
    borderRadius: 'var(--radius, 5px)',
    color: 'var(--saa-danger, #d32f2f)',
    fontSize: 'var(--saa-fs-base, 14px)',
    fontWeight: '500',
    lineHeight: '1.5',
  },
  errorIcon: {
    width: '18px',
    height: '18px',
    color: 'var(--saa-danger-strong, #dc2626)',
    flexShrink: 0,
  },
  infoSection: {
    marginBottom: '24px',
    paddingBottom: '24px',
    borderBottom: '1px solid var(--saa-border, #e5e7eb)',
  },
  infoSectionTitle: {
    fontSize: 'var(--saa-fs-lg, 18px)',
    fontWeight: '600',
    color: 'var(--saa-text-heading, #1f2937)',
    marginBottom: '16px',
    marginTop: '0',
  },
  infoGrid: {
    ...commonStyles.infoGrid,
    gap: '16px',
    marginBottom: '0',
  },
  infoItem: infoFieldItem,
  infoLabel: commonStyles.infoLabel,
  infoValue: commonStyles.infoValue,
  // --- former inline styles lifted out of the JSX ---
  infoSectionLast: {
    marginBottom: '24px',
    paddingBottom: 0,
    borderBottom: 'none',
  },
  productItemLabel: flowProductItemLabel,
  productListDivider: flowProductListDivider,
  feeList: flowFeeList,
  feeRow: flowFeeRow,
  feeLabel: flowFeeLabel,
  taxNote: flowTaxNote,
  sectionIconSuccess: {
    width: '28px',
    height: '28px',
    color: 'var(--success-color, #22c55e)',
    flexShrink: 0,
  },
  statementText: {
    fontSize: 'var(--saa-fs-base, 14px)',
    color: 'var(--saa-text-body, #374151)',
    lineHeight: '1.6',
    margin: '0 0 8px 0',
  },
  statementList: {
    fontSize: 'var(--saa-fs-base, 14px)',
    color: 'var(--saa-text-body, #374151)',
    lineHeight: '1.8',
    margin: '0 0 16px 0',
    paddingLeft: '20px',
  },
  inlineLink: {
    color: 'var(--primary-color, #056666)',
    textDecoration: 'underline',
    cursor: 'pointer',
  },
  submitContent: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
  },
  submitSpinner: {
    width: '16px',
    height: '16px',
    border: '2px solid #ffffff',
    borderTopColor: 'transparent',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
  },
};

// PAYMENT INFORMATION (application flow). Sidebar/totals/help/contact keys ALIAS the shared
// Item 0 keys (dedup); the rest are screen-local (form fields, buttons, security cluster, and
// a Payment-specific summaryCard with border:none + lighter shadow). No `...commonStyles` spread.
export const paymentInformationStyles: Record<string, React.CSSProperties> = {
  // --- reused shared keys ---
  sidebar: commonStyles.summarySidebar,
  sidebarHeader: commonStyles.summaryHeader,
  sidebarIcon: commonStyles.summaryHeaderIcon,
  sidebarTitle: commonStyles.summaryHeaderTitle,
  productList: commonStyles.summaryProductList,
  productItem: commonStyles.summaryProductItem,
  productName: commonStyles.summaryProductName,
  productPrice: commonStyles.summaryProductPrice,
  divider: commonStyles.summaryDivider,
  totalRow: commonStyles.totalRow,
  totalLabel: commonStyles.totalLabel,
  totalAmount: commonStyles.totalAmount,
  helpCard: commonStyles.helpCard,
  helpTitle: commonStyles.helpTitle,
  helpDescription: commonStyles.helpDescription,
  contactInfo: commonStyles.contactInfo,
  contactItem: commonStyles.contactItem,
  contactIcon: commonStyles.contactIcon,
  contactText: commonStyles.contactText,
  contactLink: commonStyles.contactLink,
  required: commonStyles.required,
  // --- screen-local keys ---
  page: flowPage,
  backgroundDecoration: flowBackgroundDecoration,
  container: flowContainer,
  contentWrapper: flowContentWrapper,
  mainContent: flowMainContent,
  contentHeader: {
    marginBottom: '40px',
    textAlign: 'center' as const,
  },
  title: {
    fontSize: 'var(--saa-fs-display, 40px)',
    lineHeight: '1.15',
    fontWeight: '700',
    color: 'var(--saa-text-heading, #212426)',
    margin: '0 0 1rem 0',
    letterSpacing: '-0.025em',
  },
  subtitle: {
    fontSize: 'var(--saa-fs-md, 16px)',
    color: 'var(--saa-text-muted, #6b7280)',
    maxWidth: '600px',
    margin: '0 auto',
    lineHeight: '1.6',
  },
  sectionContainer: {
    backgroundColor: '#fff',
    padding: '32px',
    borderRadius: 'var(--radius-lg, 15px)',
    boxShadow: 'var(--saa-shadow-card)',
    marginBottom: '24px',
    border: '1px solid var(--saa-border, #e5e7eb)',
    transition: 'all 0.3s ease',
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '24px',
    paddingBottom: '16px',
    borderBottom: '2px solid var(--saa-border, #e5e7eb)',
  },
  sectionIcon: flowSectionIcon,
  fieldContainer: {
    marginBottom: '20px',
  },
  fieldLabel: {
    display: 'block',
    fontSize: 'var(--saa-fs-base, 14px)',
    fontWeight: '500',
    color: 'var(--saa-text-body, #374151)',
    marginBottom: '6px',
  },
  fieldInput: {
    width: '100%',
    padding: '12px 16px',
    fontSize: 'var(--saa-fs-md, 16px)',
    border: '2px solid var(--saa-border, #e5e7eb)',
    borderRadius: 'var(--radius, 5px)',
    boxSizing: 'border-box' as const,
    fontFamily: 'inherit',
    backgroundColor: '#fff',
    color: 'var(--saa-text-heading, #1f2937)',
    transition: 'all 0.2s ease',
    outline: 'none',
  },
  sectionTitle: commonStyles.sectionTitle,
  sectionDescription: {
    fontSize: 'var(--saa-fs-base, 14px)',
    color: 'var(--saa-text-muted, #6b7280)',
    marginTop: '-12px',
    marginBottom: '24px',
    lineHeight: '1.6',
  },
  navigation: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: '16px',
    marginTop: '32px',
    width: '100%',
  },
  buttonPrimary: {
    padding: '14px 36px',
    background: 'linear-gradient(135deg, var(--primary-color, #056666) 0%, var(--primary-color, #056666) 100%)',
    color: '#fff',
    border: 'none',
    borderRadius: 'var(--radius, 5px)',
    fontSize: 'var(--saa-fs-md, 16px)',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    boxShadow: 'var(--saa-shadow-focus)',
  },
  buttonSecondary: {
    padding: '12px 32px',
    background: 'transparent',
    color: 'var(--saa-text-soft, #4b5563)',
    border: 'none',
    borderRadius: 'var(--radius, 5px)',
    fontSize: 'var(--saa-fs-base, 14px)',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  buttonSecondaryHover: {
    background: 'rgba(54, 70, 245, 0.08)',
  },
  errorContainer: {
    maxWidth: '600px',
    margin: '100px auto',
    padding: '40px',
    backgroundColor: '#fff',
    borderRadius: 'var(--radius, 5px)',
    boxShadow: 'var(--saa-shadow-card)',
    textAlign: 'center' as const,
  },
  errorTitle: commonStyles.errorTitle,
  button: commonStyles.button,
  errorMessage: {
    color: 'var(--saa-danger, #d32f2f)',
    fontSize: 'var(--saa-fs-sm, 13px)',
    marginTop: '6px',
    marginBottom: '0',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    fontWeight: '600',
  },
  summaryCard: {
    backgroundColor: '#fff',
    padding: '24px',
    borderRadius: 'var(--radius-lg, 15px)',
    boxShadow: 'var(--saa-shadow-card)',
    border: 'none',
  },
  securityInfo: {
    backgroundColor: 'color-mix(in srgb, var(--primary-color, #056666) 10%, white)',
    padding: '16px',
    borderRadius: 'var(--radius, 5px)',
    border: '1px solid color-mix(in srgb, var(--primary-color, #056666) 20%, white)',
    marginTop: '16px',
  },
  securityHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    marginBottom: '8px',
  },
  securityIcon: {
    width: '20px',
    height: '20px',
    color: 'var(--primary-color, #056666)',
    flexShrink: 0,
  },
  securityTitle: {
    fontSize: 'var(--saa-fs-base, 14px)',
    fontWeight: '600',
    color: 'var(--saa-text-heading, #1f2937)',
    margin: '0',
  },
  securityDescription: {
    fontSize: 'var(--saa-fs-base, 14px)',
    color: 'var(--saa-text-muted, #6b7280)',
    margin: '0',
    lineHeight: '1.6',
  },
  // --- former inline styles lifted out of the JSX ---
  fieldInputError: {
    width: '100%',
    padding: '12px 16px',
    fontSize: 'var(--saa-fs-md, 16px)',
    border: '2px solid var(--saa-danger, #d32f2f)',
    borderRadius: 'var(--radius, 5px)',
    boxSizing: 'border-box' as const,
    fontFamily: 'inherit',
    backgroundColor: '#fff',
    color: 'var(--saa-text-heading, #1f2937)',
    transition: 'all 0.2s ease',
    outline: 'none',
  },
  productItemLabel: flowProductItemLabel,
  productListDivider: flowProductListDivider,
  feeList: flowFeeList,
  feeRow: flowFeeRow,
  feeLabel: flowFeeLabel,
  taxNote: flowTaxNote,
};

// PERSONENAUTO (product screen). Spreads commonStyles + overrides + screen-unique keys
// (license plate, addition selector, address options, debug panel). No CSS on the page.
export const personenAutoStyles: Record<string, React.CSSProperties> = {
  ...commonStyles,
  page: {
    flex: 1,
    backgroundColor: 'var(--background-dark, #F5F5F5)',
    padding: '40px 20px',
  },
  container: commonStyles.container,
  // (5) the (i) icon flows inline right after the label text — in the base flex layout
  // a WRAPPED label span fills the whole line and shoves the icon to the far edge.
  toggleLabel: {
    ...commonStyles.toggleLabel,
    display: 'block',
    overflowWrap: 'anywhere' as const,
    minWidth: 0,
  },
  // position: static — the info popup anchors to the toggle CARD (relative, below),
  // not to this tiny icon; icon-anchored, the 320px popup ran off the right edge.
  infoButtonWrapper: {
    ...commonStyles.infoButtonWrapper,
    position: 'static' as const,
    display: 'inline-flex',
    verticalAlign: 'middle',
    marginLeft: '6px',
  },
  toggleRow: propToggleRowRelative,
  // Binds a label's tail (last word, or last few chars of a long single word) + (i) icon — they
  // wrap together so the icon never lands alone on its own line. Single-sourced from commonStyles.
  labelTailNoWrap: commonStyles.labelTailNoWrap,
  // Left label column: minWidth:0 lets it shrink so a long label wraps (word splits at a natural
  // boundary) instead of pushing the badge/toggle out or down. The (i) icon stays inline with the
  // text (glued to the label's last atom), on the left — like Rechtsbijstand / Pechhulp.
  toggleLabelCol: propToggleLabelCol,
  addressTitle: {
    fontSize: 'var(--saa-fs-xl, 20px)',
    fontWeight: '600',
    color: 'var(--saa-text-heading, #1f2937)',
    margin: 0,
    paddingLeft: '12px',
  },
  addressFieldSmall: {
    width: '120px',
    marginBottom: '16px',
  },
  // LICENSE PLATE — real-world artifact, NOT brand styling. A Dutch plate is
  // yellow (#f6c700) with black characters and the blue EU band (#003399);
  // these values are legally fixed and stay LITERAL, like the geometric 50%
  // radii. Do not tokenize in any future sweep.
  licensePlate: {
    display: 'inline-flex',
    alignItems: 'stretch',
    width: '260px',
    height: '48px',
    borderRadius: '4px',
    border: '2px solid #1a1a1a',
    overflow: 'hidden',
    boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
  },
  licensePlateBand: {
    width: '32px',
    minWidth: '32px',
    backgroundColor: '#003399',
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    gap: '1px',
    padding: '4px 0 3px',
  },
  licensePlateEuEmblem: {
    width: '20px',
    height: '20px',
    position: 'relative' as const,
  },
  licensePlateCountry: {
    fontSize: '12px',
    fontWeight: 700,
    color: '#ffffff',
    letterSpacing: '0.5px',
    lineHeight: 1,
    marginTop: '1px',
  },
  licensePlateInput: {
    flex: 1,
    border: 'none',
    outline: 'none',
    backgroundColor: '#f6c700',
    color: '#1a1a1a',
    fontSize: '22px',
    fontWeight: 700,
    fontFamily: "'Arial Black', 'Impact', sans-serif",
    letterSpacing: '3px',
    textTransform: 'uppercase' as const,
    textAlign: 'center' as const,
    padding: '0 8px',
    margin: 0,
    width: '100%',
    boxSizing: 'border-box' as const,
  },
  sectionDivider: {
    border: 'none',
    borderTop: '2px solid var(--saa-border, #e5e7eb)',
    margin: '24px 0',
  },
  fieldContainer: {
    marginBottom: '16px',
  },
  fieldSelect: {
    ...formControlBase,
    ...dropdownChrome,
    transition: 'border-color 0.2s',
  },
  navigation: sharedNavigation,
  buttonPrimary: commonStyles.buttonPrimary,
  buttonSecondary: commonStyles.buttonSecondary,
  buttonSecondaryHover: commonStyles.buttonSecondaryHover,
  additionSelectorIcon: {
    width: '20px',
    height: '20px',
    minWidth: '20px',
    maxWidth: '20px',
    minHeight: '20px',
    maxHeight: '20px',
    color: 'var(--saa-notice, #f59e0b)',
    flexShrink: 0,
  },
  calculateButton: {
    marginBottom: '20px',
    padding: '14px 32px',
    backgroundColor: 'var(--success-color, #28A745)',
    color: '#fff',
    border: 'none',
    borderRadius: 'var(--radius, 5px)',
    fontSize: 'var(--saa-fs-md, 16px)',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s',
    boxShadow: 'var(--saa-shadow-card)',
    width: '100%',
  },
  debugPanel: propDebugPanel,
  coveragePremiumBadge: {
    fontSize: 'var(--saa-fs-base, 14px)',
    fontWeight: '600',
    color: 'var(--primary-color, #056666)',
    backgroundColor: 'color-mix(in srgb, var(--primary-color, #056666) 10%, white)',
    padding: '4px 10px',
    borderRadius: 'var(--radius-lg, 15px)',
    whiteSpace: 'nowrap' as const,
  },
  coverageMatrixToggle: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '6px 12px',
    marginLeft: 'auto',
    fontSize: 'var(--saa-fs-sm, 13px)',
    fontWeight: 500,
    color: 'var(--saa-text-body, #374151)',
    backgroundColor: '#ffffff',
    border: '1px solid var(--saa-border-strong, #d1d5db)',
    borderRadius: 'var(--radius, 5px)',
    cursor: 'pointer',
  },
  coverageMatrixHeaderRow: {
    borderBottom: '1px solid var(--saa-border, #e5e7eb)',
    paddingBottom: '8px',
    marginBottom: '4px',
    alignItems: 'center',
  },
  coverageMatrixHeader: {
    fontSize: 'var(--saa-fs-sm, 13px)',
    fontWeight: 600,
    color: 'var(--saa-text-muted, #6b7280)',
    textAlign: 'center' as const,
    padding: '6px 4px',
    borderRadius: 'var(--radius-pill, 50px)',
  },
  coverageMatrixHeaderActive: {
    color: 'var(--primary-color, #056666)',
    backgroundColor: 'color-mix(in srgb, var(--primary-color, #056666) 12%, white)',
  },
  coverageMatrixRow: {
    padding: '8px 0',
    alignItems: 'center',
  },
  coverageMatrixLabel: {
    fontSize: 'var(--saa-fs-sm, 13px)',
    color: 'var(--saa-text-body, #374151)',
    paddingLeft: '4px',
  },
  coverageMatrixCell: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '4px',
    borderLeft: '2px solid transparent',
    borderRight: '2px solid transparent',
  },
  coverageMatrixCellActive: {
    borderLeft: '2px solid color-mix(in srgb, var(--primary-color, #056666) 30%, white)',
    borderRight: '2px solid color-mix(in srgb, var(--primary-color, #056666) 30%, white)',
  },
  premiumBadge: {
    fontSize: 'var(--saa-fs-xl, 20px)',
    fontWeight: '700',
    color: 'var(--primary-color, #056666)',
    backgroundColor: 'color-mix(in srgb, var(--primary-color, #056666) 10%, white)',
    padding: '6px 14px',
    borderRadius: 'var(--radius-lg, 15px)',
    whiteSpace: 'nowrap' as const,
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    flexShrink: 0,
  },
  // --- former inline styles lifted out of the JSX ---
  // override atoms (composed with a base class via a value-free spread)
  readonlyTint: { backgroundColor: 'var(--saa-surface-muted, #f3f4f6)', color: 'var(--saa-text-muted, #6b7280)' },
  clickablePadded: { paddingLeft: '16px', cursor: 'pointer' },
  ellipsis: { overflow: 'hidden', textOverflow: 'ellipsis' },
  mr12Shrink: { marginRight: '12px', flexShrink: 0 },
  mb0: { marginBottom: 0 },
  labelBlock: { fontSize: 'var(--saa-fs-base, 14px)', marginBottom: '8px', display: 'block' },
  toggleRowColumnOverride: { marginTop: '12px', flexDirection: 'column' as const, alignItems: 'stretch', gap: '12px', padding: '20px' },
  badgeDisabled: { color: 'var(--saa-text-faint, #9ca3af)', backgroundColor: 'var(--saa-surface-muted, #f3f4f6)' },
  buttonBusy: { opacity: 0.7, cursor: 'wait' as const },
  toggleDisabled: { cursor: 'not-allowed', opacity: 0.5 },
  // standalone classes
  licensePlateError: { border: '1px solid var(--saa-danger, #d32f2f)' },
  rowGap16: { display: 'flex', gap: '16px' },
  rowGap16Top8: { display: 'flex', gap: '16px', marginTop: '8px' },
  rowGap16Start: { display: 'flex', gap: '16px', alignItems: 'flex-start' },
  hint13: { fontSize: 'var(--saa-fs-sm, 13px)', color: 'var(--saa-text-muted, #6b7280)' },
  spinnerMt: { marginTop: '10px' },
  spinnerMtTight: { marginTop: '8px' },
  coverageNoticeMt: { marginTop: '16px' },
  dividerTop: { marginTop: '16px', paddingTop: '16px', borderTop: '1px solid var(--saa-border, #e5e7eb)' },
  helperListItem: { margin: '6px 0 0 18px', padding: 0, fontWeight: 500 },
  divider10: { height: '1px', backgroundColor: 'var(--saa-border, #e5e7eb)', margin: '10px 0' },
  label15: { fontSize: 'var(--saa-fs-base, 14px)', fontWeight: 600, color: 'var(--saa-text-heading, #1f2937)' },
  label15Error: { fontSize: 'var(--saa-fs-base, 14px)', fontWeight: '600', color: 'var(--saa-danger-strong, #dc2626)' },
  warningText: { fontSize: 'var(--saa-fs-base, 14px)', color: 'var(--saa-danger, #d32f2f)', fontWeight: 500 },
  matrixValue: { fontSize: 'var(--saa-fs-sm, 13px)', fontWeight: 500, color: 'var(--saa-text-heading, #1f2937)', marginLeft: '8px', textAlign: 'right' as const },
  errorIcon20Top: { flexShrink: 0, width: '20px', height: '20px', color: 'var(--saa-danger-strong, #dc2626)', marginTop: '1px' },
  errorIcon20: { flexShrink: 0, width: '20px', height: '20px', color: 'var(--saa-danger-strong, #dc2626)' },
  rowBetweenBaseline: { display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', padding: '2px 0' },
  colGap4: { display: 'flex', flexDirection: 'column' as const, gap: '4px' },
  rowCenterWrap: { display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' as const, marginBottom: '8px' },
  rowCenter12: { display: 'flex', alignItems: 'center', gap: '12px' },
  rowCenterFlex: { display: 'flex', alignItems: 'center', flex: 1, minWidth: 0 },
  coverageGroupLabel: { display: 'block', fontSize: 'var(--saa-fs-base, 14px)', fontWeight: '600', color: 'var(--primary-color, #056666)', marginBottom: '18px' },
  errorBox: { color: 'var(--saa-danger, #d32f2f)', fontSize: 'var(--saa-fs-base, 14px)', fontWeight: 500, padding: '8px 12px', backgroundColor: 'var(--saa-danger-bg, #fff5f5)', borderRadius: 'var(--radius, 5px)' },
  vehicleSummaryCard: {
    padding: '16px 20px',
    backgroundColor: 'var(--saa-surface-subtle, #f9fafb)',
    borderRadius: 'var(--radius, 5px)',
    border: '1px solid color-mix(in srgb, var(--primary-color, #056666) 25%, transparent)',
    marginBottom: '16px',
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '0',
  },
  vehicleSummaryBadge: {
    fontSize: 'var(--saa-fs-xs, 12px)',
    fontWeight: 600,
    color: 'var(--primary-color, #056666)',
    backgroundColor: 'color-mix(in srgb, var(--primary-color, #056666) 8%, white)',
    padding: '4px 10px',
    borderRadius: 'var(--radius, 5px)',
    display: 'inline-block',
    marginBottom: '6px',
    letterSpacing: '0.3px',
  },
  vehicleSummaryGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '2px 32px',
    padding: '0 4px',
  },
  kwWarning: {
    padding: '12px 16px',
    backgroundColor: 'var(--saa-danger-bg, #fff5f5)',
    border: '1px solid #fecaca',
    borderRadius: 'var(--radius, 5px)',
    marginBottom: '16px',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  calcErrorBanner: {
    maxWidth: '900px',
    margin: '0 auto 16px auto',
    padding: '16px 20px',
    backgroundColor: 'var(--saa-danger-bg, #fff5f5)',
    border: '1px solid #fecaca',
    borderRadius: 'var(--radius-lg, 15px)',
    boxShadow: 'var(--saa-shadow-card)',
    color: 'var(--saa-danger, #d32f2f)',
    fontSize: 'var(--saa-fs-base, 14px)',
    fontWeight: 600,
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
    boxSizing: 'border-box' as const,
  },
};

// WOONHUIS (product screen). Spreads commonStyles + overrides + screen-unique keys.
// Selectable coverage-option card (CoverageOptionCard component) — top row holds the
// radio with the price badge immediately beside it; the title gets its own full-width
// line below (may wrap freely), then the description. Nothing can collide.
export const coverageOptionCardStyles: Record<string, React.CSSProperties> = {
  card: {
    flex: '1 1 0%',
    minWidth: 0,
    padding: '16px',
    borderRadius: 'var(--radius, 5px)',
    border: '1.5px solid var(--saa-border, #e5e7eb)',
    backgroundColor: '#fff',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },
  cardSelected: {
    flex: '1 1 0%',
    minWidth: 0,
    padding: '16px',
    borderRadius: 'var(--radius, 5px)',
    border: '2px solid var(--primary-color, #056666)',
    backgroundColor: 'color-mix(in srgb, var(--primary-color, #056666) 4%, white)',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },
  cardDisabled: {
    flex: '1 1 0%',
    minWidth: 0,
    padding: '16px',
    borderRadius: 'var(--radius, 5px)',
    border: '1.5px solid var(--saa-border, #e5e7eb)',
    backgroundColor: 'var(--saa-surface-muted, #f9fafb)',
    cursor: 'not-allowed',
    opacity: 0.55,
    transition: 'all 0.2s ease',
  },
  topRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    marginBottom: '8px',
  },
  radio: personenAutoStyles.radioDot,
  radioSelected: personenAutoStyles.radioDotSelected,
  radioInner: personenAutoStyles.radioDotInner,
  // Normal word wrapping only — no break-word, so "Casco" can never split mid-word;
  // the auto-fit card grid guarantees enough width for whole words.
  label: {
    display: 'block' as const,
    fontSize: 'var(--saa-fs-base, 14px)',
    fontWeight: 600,
    color: 'var(--primary-color, #056666)',
    marginBottom: '6px',
  },
  labelInactive: {
    display: 'block' as const,
    fontSize: 'var(--saa-fs-base, 14px)',
    fontWeight: 500,
    color: 'var(--saa-text-body, #374151)',
    marginBottom: '6px',
  },
  price: personenAutoStyles.coveragePremiumBadge,
  priceDisabled: personenAutoStyles.badgeDisabled,
  description: {
    fontSize: 'var(--saa-fs-sm, 13px)',
    color: 'var(--saa-text-muted, #6b7280)',
    margin: 0,
    lineHeight: '1.4',
  },
};

export const woonhuisStyles: Record<string, React.CSSProperties> = {
  ...commonStyles,
  // Radio group styles for Yes/No fields
  // Shopping Cart styles
  // Monument warning styles - orange/amber theme for important notice
  addressTitle: propAddressTitle,
  // Calculation and debug panel styles
  calculateButton: {
    padding: '12px 32px',
    backgroundColor: 'var(--success-color, #28A745)',
    color: '#fff',
    border: 'none',
    borderRadius: 'var(--radius, 5px)',
    fontSize: 'var(--saa-fs-md, 16px)',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s',
    boxShadow: 'var(--saa-shadow-card)',
    width: '100%',
  },
  debugPanel: propDebugPanel,
  // Calculation banners
  coveragePremiumBadge: propCoveragePremiumBadge,
  navigation: sharedNavigation,
  // Premium summary styles
  // --- former inline styles lifted out of the JSX ---
  mb8: { marginBottom: '8px' },
  infoBtnNudge: { padding: '0 4px', marginTop: '2px' },
  infofolioFilled: { backgroundColor: 'var(--saa-surface-muted, #f3f4f6)' },
  rowCenterFlex: { display: 'flex', alignItems: 'center', flex: 1 },
  rowWrapBetween: { display: 'flex', flexWrap: 'wrap' as const, justifyContent: 'space-between', alignItems: 'center', gap: '12px' },
  rowStart6: { display: 'flex', alignItems: 'flex-start', gap: '6px' },
  hint13Gray: { fontSize: 'var(--saa-fs-sm, 13px)', color: 'var(--saa-text-soft, #4b5563)', margin: 0 },
  linkPrimary13: { color: 'var(--primary-color, #056666)', fontSize: 'var(--saa-fs-sm, 13px)', fontWeight: 500 },
};

export const workflowSummaryStyles: Record<string, React.CSSProperties> = {
  page: {
    ...commonStyles.page,
    minHeight: '100vh',
    padding: '32px 20px 64px', // own vertical rhythm; horizontal 20px matches the canonical page padding
  },
  // Shared 1150px flow container — same width as the other steps (Klantgegevens,
  // product selection). Below the lg breakpoint index.css caps .summary-container
  // to a 768px reading width for the single-column stack.
  container: commonStyles.container,
  contentHeader: {
    ...commonStyles.contentHeader,
    marginBottom: '48px',
  },
  title: {
    ...commonStyles.title,
    fontSize: 'var(--saa-fs-display, 36px)',
  },
  subtitle: {
    ...commonStyles.subtitle,
    fontSize: 'var(--saa-fs-md, 16px)',
    maxWidth: '600px',
    margin: '0 auto',
  },
  // Summary-specific styles
  successBadge: commonStyles.successBadge,
  successIcon: commonStyles.successIcon,
  grid: {
    ...commonStyles.grid,
    gridTemplateColumns: '1fr 350px',
    // `start` keeps both column stacks at their natural content height.
    alignItems: 'start',
    columnGap: '20px',
    rowGap: '0',
  },
  // Shared wrapper for both desktop columns (left: gegevens + documenten, right:
  // premium + help). Flex column keeps the card margins deterministic (no margin
  // collapse through the wrapper); mobile dissolves it via display: contents.
  columnStack: {
    display: 'flex',
    flexDirection: 'column' as const,
    minWidth: 0,
  },
  // Use formContainer from commonStyles
  card: {
    ...commonStyles.formContainer,
    padding: '24px',
    display: 'flex',
    flexDirection: 'column' as const,
  },
  premiumCard: {
    ...commonStyles.formContainer,
    padding: '16px 20px',
    display: 'flex',
    flexDirection: 'column' as const,
  },
  cardTitle: {
    ...commonStyles.sectionTitle,
    fontSize: 'var(--saa-fs-xl, 20px)',
    marginBottom: '16px',
  },
  infoGrid: {
    ...commonStyles.infoGrid,
    gap: '16px',
    marginBottom: '16px',
  },
  infoItem: infoFieldItem,
  infoLabel: commonStyles.infoLabel,
  infoValue: commonStyles.infoValue,
  premiumHeader: {
    ...commonStyles.premiumHeader,
    marginBottom: '8px',
  },
  premiumTitle: commonStyles.premiumTitle,
  premiumAmountContainer: {
    ...commonStyles.premiumAmountContainer,
    marginBottom: '8px',
  },
  premiumAmountLarge: {
    ...commonStyles.premiumAmountLarge,
    fontSize: 'var(--saa-fs-display, 36px)', // Increased from default
    fontWeight: '700',
  } as React.CSSProperties,
  premiumPeriodText: commonStyles.premiumPeriodText,
  divider: {
    ...commonStyles.divider,
    marginBottom: '8px',
  },
  detailsList: {
    ...commonStyles.detailsList,
    gap: '12px',
  },
  detailItem: commonStyles.detailItem,
  detailLabel: commonStyles.detailLabel,
  detailValue: commonStyles.detailValue,
  bottomNav: commonStyles.screenNav,
  bottomNavBack: commonStyles.buttonSecondary,
  bottomNavBackHover: commonStyles.buttonSecondaryHover,
  // Divider between the personal-info block and the insured-object / product sections.
  innerDivider: {
    height: '1px',
    backgroundColor: 'var(--saa-border, #e5e7eb)',
    margin: '16px 0 20px',
  } as React.CSSProperties,
  // Insured-object panels (property and vehicle) — same visual treatment for both so the
  // page reads as "here is your insured thing" regardless of product group.
  insuredObjectBlock: {
    background: 'linear-gradient(135deg, color-mix(in srgb, var(--primary-color, #056666) 5%, white) 0%, color-mix(in srgb, var(--primary-color, #056666) 8%, white) 100%)',
    borderRadius: 'var(--radius-lg, 15px)',
    padding: '16px 20px',
    marginBottom: '20px',
    border: '1px solid rgba(5, 102, 102, 0.15)',
  } as React.CSSProperties,
  insuredObjectHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    marginBottom: '12px',
  } as React.CSSProperties,
  insuredObjectTitle: {
    fontSize: 'var(--saa-fs-base, 14px)',
    fontWeight: 600,
    color: 'var(--primary-color, #056666)',
  } as React.CSSProperties,
  insuredObjectGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '8px 24px',
  } as React.CSSProperties,
  insuredObjectLabel: {
    fontSize: 'var(--saa-fs-xs, 12px)',
    color: 'var(--saa-text-faint, #9ca3af)',
    display: 'block',
  } as React.CSSProperties,
  insuredObjectValue: {
    fontSize: 'var(--saa-fs-base, 14px)',
    fontWeight: 500,
    color: 'var(--saa-text-heading, #1f2937)',
  } as React.CSSProperties,
  // Aanvragen button pinned inside the premium card (was previously its own convert-card).
  premiumCardCta: {
    ...commonStyles.buttonSecondary,
    justifyContent: 'center',
    width: '100%',
    padding: '14px 20px',
    marginTop: '16px',
    backgroundColor: 'var(--primary-color, #056666)',
    color: '#fff',
    borderRadius: 'var(--radius-lg, 15px)',
    fontSize: 'var(--saa-fs-md, 16px)',
    fontWeight: 600,
    boxShadow: 'var(--saa-shadow-focus)',
    transition: 'all 0.2s ease',
    whiteSpace: 'nowrap' as const,
  } as React.CSSProperties,
  // Dev-only debug panel — mirrors InboedelScreen's pattern so the toggle feel is consistent.
  debugPanel: {
    backgroundColor: '#fff',
    padding: '20px',
    borderRadius: 'var(--radius-lg, 15px)',
    boxShadow: 'var(--saa-shadow-card)',
    marginTop: '32px',
    marginBottom: '30px',
    border: '1px dashed var(--saa-border-strong, #d1d9dc)',
  } as React.CSSProperties,
  debugPanelHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px',
    paddingBottom: '12px',
    borderBottom: '1px solid var(--saa-border-soft, #e0e0e0)',
  } as React.CSSProperties,
  debugPanelTitle: {
    margin: 0,
    fontSize: 'var(--saa-fs-lg, 18px)',
    fontWeight: 600,
    color: 'var(--saa-text-heading, #1f2937)',
  } as React.CSSProperties,
  debugToggleButton: {
    padding: '6px 12px',
    backgroundColor: 'var(--background-dark, #F5F5F5)',
    color: 'var(--saa-text-heading, #1f2937)',
    border: '1px solid var(--saa-border-strong, #d1d5db)',
    borderRadius: 'var(--radius, 5px)',
    fontSize: 'var(--saa-fs-base, 14px)',
    cursor: 'pointer',
    fontWeight: 500,
  } as React.CSSProperties,
  convertButtonDisabled: {
    opacity: 0.5,
    cursor: 'not-allowed',
    backgroundColor: 'var(--saa-surface-muted, #f3f4f6)',
    pointerEvents: 'none' as const,
  } as React.CSSProperties,
  convertButtonIcon: {
    ...commonStyles.convertButtonIcon,
    width: '20px',
    height: '20px',
  },
  // Chain status inside the premium card: one in-flight row, one error box.
  chainStatusRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    margin: '0 0 16px',
    fontSize: 'var(--saa-fs-sm, 13px)',
    color: 'var(--saa-text-muted, #57666b)',
  } as React.CSSProperties,
  chainErrorBox: {
    backgroundColor: 'var(--saa-danger-bg, #fff5f5)',
    color: 'var(--saa-danger, #d32f2f)',
    borderRadius: 'var(--radius, 5px)',
    padding: '12px 14px',
    margin: '0 0 16px',
    fontSize: 'var(--saa-fs-sm, 13px)',
    lineHeight: '1.5',
  } as React.CSSProperties,
  chainErrorLink: {
    color: 'var(--saa-danger, #d32f2f)',
    fontWeight: '600',
    textDecoration: 'underline',
    whiteSpace: 'nowrap' as const,
  } as React.CSSProperties,
  documentsCard: {
    backgroundColor: '#ffffff',
    border: '1px solid var(--saa-border, #e5e7eb)',
    borderRadius: 'var(--radius-lg, 15px)',
    padding: '24px',
    marginBottom: '24px',
  } as React.CSSProperties,
  documentsTitle: {
    fontSize: 'var(--saa-fs-lg, 18px)',
    fontWeight: '600',
    color: 'var(--saa-text-heading, #1f2937)',
    marginBottom: '8px',
  } as React.CSSProperties,
  documentsSubtitle: {
    fontSize: 'var(--saa-fs-base, 14px)',
    color: 'var(--saa-text-muted, #6b7280)',
    marginBottom: '16px',
  } as React.CSSProperties,
  documentsList: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '12px',
  } as React.CSSProperties,
  documentsContainer: {
    display: 'flex',
    flexDirection: 'column' as const,
  } as React.CSSProperties,
  documentLink: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px 16px',
    backgroundColor: 'var(--saa-surface-subtle, #f9fafb)',
    border: '1px solid var(--saa-border, #e5e7eb)',
    borderRadius: 'var(--radius, 5px)',
    textDecoration: 'none',
    transition: 'all 0.2s ease',
    cursor: 'pointer',
  } as React.CSSProperties,
  documentIcon: {
    width: '24px',
    height: '24px',
    color: 'var(--primary-color, #056666)',
    flexShrink: 0,
  } as React.CSSProperties,
  documentLinkMissing: {
    backgroundColor: 'var(--saa-notice-bg, #fffbeb)',
    border: '1px dashed var(--saa-notice, #f59e0b)',
    cursor: 'default',
  } as React.CSSProperties,
  documentLinkContent: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '2px',
  } as React.CSSProperties,
  documentLinkText: {
    fontSize: 'var(--saa-fs-base, 14px)',
    fontWeight: '500',
    color: 'var(--saa-text-heading, #1f2937)',
  } as React.CSSProperties,
  documentLinkSubtext: {
    fontSize: 'var(--saa-fs-sm, 13px)',
    color: 'var(--saa-text-muted, #6b7280)',
  } as React.CSSProperties,
  expandToggleConnected: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px 20px',
    background: 'linear-gradient(135deg, var(--saa-surface-subtle, #f9fafb) 0%, var(--saa-surface-subtle, #f9fafb) 100%)',
    border: 'none',
    borderTopLeftRadius: '0',
    borderTopRightRadius: '0',
    borderBottomLeftRadius: '10px',
    borderBottomRightRadius: '10px',
    cursor: 'pointer',
    transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
    boxShadow: 'var(--saa-shadow-control)',
  } as React.CSSProperties,
  expandToggleHover: {
    background: 'linear-gradient(135deg, color-mix(in srgb, var(--primary-color, #056666) 10%, white) 0%, color-mix(in srgb, var(--primary-color, #056666) 15%, white) 100%)',
    transform: 'translateY(-1px)',
  } as React.CSSProperties,
  expandToggleLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '14px',
  } as React.CSSProperties,
  expandToggleIconWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '36px',
    height: '36px',
    borderRadius: 'var(--radius, 5px)',
    background: 'linear-gradient(135deg, #ffffff 0%, var(--saa-surface-subtle, #f9fafb) 100%)',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.04), inset 0 1px 0 rgba(255, 255, 255, 0.8)',
    border: 'none',
    transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
  } as React.CSSProperties,
  expandToggleIcon: {
    width: '18px',
    height: '18px',
    color: 'var(--saa-text-muted, #57666b)',
    transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), color 0.2s ease',
  } as React.CSSProperties,
  expandToggleText: {
    fontSize: 'var(--saa-fs-base, 14px)',
    fontWeight: '600',
    color: 'var(--saa-text-muted, #57666b)',
    letterSpacing: '-0.01em',
  } as React.CSSProperties,
  expandToggleBadge: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: '28px',
    height: '28px',
    padding: '0 10px',
    borderRadius: 'var(--radius-lg, 15px)',
    background: 'linear-gradient(135deg, var(--primary-color, #056666) 0%, var(--primary-color, #056666) 100%)',
    fontSize: 'var(--saa-fs-sm, 13px)',
    fontWeight: '700',
    color: '#ffffff',
    boxShadow: 'var(--saa-shadow-focus)',
    letterSpacing: '0.02em',
    transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
  } as React.CSSProperties,
  // Collapsible documents container
  collapsibleDocumentsContainer: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '10px',
    padding: '16px',
    backgroundColor: 'var(--saa-surface-subtle, #f9fafb)',
    border: 'none',
    borderBottomLeftRadius: '10px',
    borderBottomRightRadius: '10px',
  } as React.CSSProperties,
  // --- premium breakdown (classic, non-USE_BREAKDOWN_UI branch) ---
  premiumDisplayColumn: {
    display: 'flex',
    flexDirection: 'column' as const,
  } as React.CSSProperties,
  subtotalRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '6px 10px',
    marginBottom: '4px',
  } as React.CSSProperties,
  subtotalText: {
    fontSize: 'var(--saa-fs-base, 14px)',
    color: 'var(--saa-text-muted, #6b7280)',
  } as React.CSSProperties,
  discountRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '12px',
    padding: '6px 10px',
    backgroundColor: 'color-mix(in srgb, var(--primary-color, #056666) 10%, white)',
    borderRadius: 'var(--radius, 5px)',
  } as React.CSSProperties,
  discountRowLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  } as React.CSSProperties,
  discountRowIcon: {
    width: '14px',
    height: '14px',
    color: 'var(--primary-color, #056666)',
  } as React.CSSProperties,
  discountRowLabel: {
    fontSize: 'var(--saa-fs-sm, 13px)',
    color: 'var(--primary-color, #056666)',
    fontWeight: 500,
  } as React.CSSProperties,
  discountRowValue: {
    fontSize: 'var(--saa-fs-sm, 13px)',
    color: 'var(--primary-color, #056666)',
    fontWeight: 600,
  } as React.CSSProperties,
  miniDivider: {
    height: '1px',
    backgroundColor: 'var(--saa-border, #e5e7eb)',
    margin: '4px 10px 8px',
  } as React.CSSProperties,
  feeList: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '2px',
    padding: '0 10px',
    marginBottom: '8px',
  } as React.CSSProperties,
  feeRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  } as React.CSSProperties,
  feeText: {
    fontSize: 'var(--saa-fs-xs, 12px)',
    color: 'var(--saa-text-faint, #9ca3af)',
  } as React.CSSProperties,
  taxNote: {
    fontSize: 'var(--saa-fs-2xs, 11px)',
    color: 'var(--saa-text-faint, #9ca3af)',
    marginTop: '4px',
  } as React.CSSProperties,
  loadingPremiumColumn: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100px',
    gap: '12px',
  } as React.CSSProperties,
  loadingPremiumSpinner: {
    width: '40px',
    height: '40px',
    border: '4px solid rgba(0, 0, 0, 0.1)',
    borderTop: '4px solid var(--primary-color, #056666)',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
  } as React.CSSProperties,
  // --- product-documents collapsible state variants ---
  expandToggleExpanded: {
    borderBottomLeftRadius: '0',
    borderBottomRightRadius: '0',
    boxShadow: 'none',
    background: 'var(--saa-surface-subtle, #f9fafb)',
  } as React.CSSProperties,
  expandToggleIconWrapperHover: {
    background: 'linear-gradient(135deg, #ffffff 0%, var(--saa-surface-subtle, #f9fafb) 100%)',
  } as React.CSSProperties,
  expandToggleIconExpanded: {
    transform: 'rotate(180deg)',
  } as React.CSSProperties,
  expandToggleIconHover: {
    color: 'var(--saa-text-muted, #57666b)',
  } as React.CSSProperties,
  expandToggleTextHover: {
    color: 'var(--saa-text-body, #373F47)',
  } as React.CSSProperties,
  expandToggleBadgeHover: {
    boxShadow: 'var(--saa-shadow-focus)',
    transform: 'scale(1.05)',
  } as React.CSSProperties,
  collapsibleDocsAnimator: {
    overflow: 'hidden',
    transition: 'max-height 0.3s ease-in-out',
  } as React.CSSProperties,
  collapsibleDocsOpen: {
    maxHeight: '2000px',
  } as React.CSSProperties,
  collapsibleDocsClosed: {
    maxHeight: '0',
  } as React.CSSProperties,
};

export const inboedelStyles: Record<string, React.CSSProperties> = {
  ...commonStyles,
  // Override navigation to constrain button width
  navigation: sharedNavigation,
  // Page-specific styles unique to InboedelScreen
  errorBanner: sharedErrorBanner,
  // Infofolio-locked fields: value came from the lookup, editing is off. Must
  // read as "locked", not "broken": muted surface + text, lock cursor.
  fieldInputDisabled: {
    backgroundColor: 'var(--saa-surface-muted, #f3f4f6)',
    color: 'var(--saa-text-muted, #57666b)',
    borderColor: 'var(--saa-border, #dce3e5)',
    cursor: 'not-allowed',
  },
  addressFieldSmall: {
    width: '120px',
  },
  addressTitle: propAddressTitle,
  calculateButton: {
    marginTop: '24px',
    padding: '14px 32px',
    backgroundColor: 'var(--success-color, #28A745)',
    color: '#fff',
    border: 'none',
    borderRadius: 'var(--radius, 5px)',
    fontSize: 'var(--saa-fs-md, 16px)',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s',
    boxShadow: 'var(--saa-shadow-card)',
    width: '100%',
  },
  debugPanel: propDebugPanel,
  additionSelectorContainer: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '16px',
    padding: '20px',
    backgroundColor: 'var(--background-dark, #F5F5F5)',
    border: '2px solid var(--saa-border, #e5e7eb)',
    borderRadius: 'var(--radius, 5px)',
    marginTop: '16px',
    marginBottom: '16px',
  },
  additionSelectorHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  additionSelectorIcon: {
    width: '20px',
    height: '20px',
    color: 'var(--primary-color, #056666)',
    flexShrink: 0,
  },
  additionSelectorText: {
    fontSize: 'var(--saa-fs-base, 14px)',
    color: 'var(--saa-text-body, #374151)',
    fontWeight: '600',
  },
  addressOption: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    padding: '16px',
    backgroundColor: '#fff',
    border: '2px solid var(--saa-border, #e5e7eb)',
    borderRadius: 'var(--radius, 5px)',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    boxShadow: 'var(--saa-shadow-control)',
  },
  addressOptionRadio: {
    width: '24px',
    height: '24px',
    borderRadius: '50%',
    border: '2px solid var(--primary-color, #056666)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    backgroundColor: '#fff',
  },
  addressOptionRadioInner: {
    width: '12px',
    height: '12px',
    borderRadius: '50%',
    backgroundColor: 'transparent',
  },
  addressOptionLabel: {
    fontSize: 'var(--saa-fs-base, 14px)',
    color: 'var(--saa-text-heading, #1f2937)',
    fontWeight: '500',
    flex: 1,
  },
  coveragePremiumBadge: { ...propCoveragePremiumBadge, flexShrink: 0 },
  toggleLabel: {
    ...commonStyles.toggleLabel,
    display: 'block',
    overflowWrap: 'anywhere' as const,
    minWidth: 0,
  },
  toggleLabelCol: propToggleLabelCol,
  toggleRow: propToggleRowRelative,
  // position: static — the popup anchors to the toggle CARD (relative, above), not to this
  // tiny icon; icon-anchored, the popup dangled over the neighbouring cards.
  infoButtonWrapper: {
    ...commonStyles.infoButtonWrapper,
    position: 'static' as const,
    display: 'inline-flex',
    verticalAlign: 'middle' as const,
  },
  toggleSwitch: { ...commonStyles.toggleSwitch, flexShrink: 0 },
  // Premium summary styles
  // --- inline-literal keys migrated from JSX (CSS centralization) ---
  colFull: { display: 'flex', flexDirection: 'column' as const, width: '100%' },
  opacityFull: { opacity: 1 },
  dividedSection: { marginTop: '16px', paddingTop: '16px', borderTop: '1px solid var(--saa-border, #e5e7eb)' },
  fieldLabelBlock: { ...commonStyles.fieldLabel, fontSize: 'var(--saa-fs-base, 14px)', marginBottom: '8px', display: 'block' },
  sectionContainerMb16: { ...commonStyles.sectionContainer, marginBottom: '16px' },
  coverageHeaderRow: { display: 'flex', flexWrap: 'wrap' as const, justifyContent: 'space-between', alignItems: 'center', gap: '12px' },
  primaryLabel13: { color: 'var(--primary-color, #056666)', fontSize: 'var(--saa-fs-sm, 13px)', fontWeight: 500 },
  rowCenter12Flex1: { display: 'flex', alignItems: 'center', gap: '12px', flex: 1 },
  rowCenter10Mr12: { display: 'flex', alignItems: 'center', gap: '10px', marginRight: '12px' },
  infofolioBadge: { display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: 'var(--saa-fs-2xs, 11px)', fontWeight: 600, color: 'var(--primary-color, #056666)', backgroundColor: 'color-mix(in srgb, var(--primary-color, #056666) 12%, white)', padding: '4px 10px', borderRadius: 'var(--radius-pill, 50px)', textTransform: 'uppercase' as const, letterSpacing: '0.4px' },
  fullDivider: { borderTop: '1px solid var(--saa-border-soft, #e0e0e0)', margin: '16px 0', width: '100%' },
  coveragePremiumBadgePending: { backgroundColor: 'var(--saa-surface-muted, #f3f4f6)', color: 'var(--saa-text-muted, #6b7280)' },
  coverageUnavailableText: { fontSize: 'var(--saa-fs-sm, 13px)', color: 'var(--saa-text-muted, #6b7280)', lineHeight: 1.5, margin: '8px 0 0' },
  // --- dynamic-state variant keys (replace inline ternary literals) ---
  addressHintMt8: { marginTop: '8px' },
  addressHintMt10: { marginTop: '10px' },
  // --- shared utility keys (sibling groups define their own copies; FINAL pass promotes to commonStyles) ---
};

export const selectFieldStyles: Record<string, React.CSSProperties> = {
  field: { marginBottom: '20px' },
  label: {
    display: 'block',
    fontSize: 'var(--saa-fs-base, 14px)',
    fontWeight: 500,
    color: 'var(--saa-text-body, #374151)',
    marginBottom: '6px',
  },
  required: { color: 'var(--saa-danger, #d32f2f)' },
  select: {
    ...formControlBase,
    ...dropdownChrome,
  },
  selectError: { border: '1px solid var(--saa-danger, #d32f2f)', backgroundColor: 'var(--saa-danger-bg, #fff5f5)' },
  selectDisabled: { backgroundColor: 'var(--saa-surface-muted, #f3f4f6)', cursor: 'not-allowed' as const },
  errorMessage: {
    fontSize: 'var(--saa-fs-sm, 13px)',
    color: 'var(--saa-danger, #d32f2f)',
    marginTop: '6px',
    marginBottom: '0',
    fontWeight: 600,
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
};

export const debugPanelStyles: Record<string, React.CSSProperties> = {
  panel: propDebugPanel,
  header: dbgHeader,
  title: { margin: 0, fontSize: 'var(--saa-fs-xl, 20px)', fontWeight: 600, color: 'var(--saa-text-heading, #1f2937)' },
  toggle: dbgToggle,
  content: { display: 'flex', flexDirection: 'column' as const, gap: '20px' },
  calcButton: {
    marginBottom: '20px',
    padding: '14px 32px',
    backgroundColor: 'var(--success-color, #28A745)',
    color: '#fff',
    border: 'none',
    borderRadius: 'var(--radius, 5px)',
    fontSize: 'var(--saa-fs-md, 16px)',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.2s',
    boxShadow: 'var(--saa-shadow-card)',
    width: '100%',
  },
  calcButtonDisabled: {
    backgroundColor: 'var(--saa-text-faint, #9ca3af)',
    cursor: 'not-allowed',
    opacity: 0.7,
  },
  errorBox: {
    marginBottom: '16px',
    padding: '12px 14px',
    backgroundColor: 'var(--saa-danger-bg, #fef2f2)',
    color: 'var(--saa-danger, #b91c1c)',
    border: '1px solid var(--saa-danger, #dc2626)',
    borderRadius: 'var(--radius, 5px)',
    fontSize: 'var(--saa-fs-base, 14px)',
  },
  section: dbgSection,
  sectionTitle: { margin: '0 0 12px 0', fontSize: 'var(--saa-fs-md, 16px)', fontWeight: 600, color: 'var(--saa-text-soft, #4b5563)' },
  pre: dbgPre,
};

export const personalInfoSectionStyles: Record<string, React.CSSProperties> = {
  container: sectContainer,
  header: sectHeader,
  iconContainer: commonStyles.sectionIconContainer,
  title: sectTitle,
};

export const addressSectionStyles: Record<string, React.CSSProperties> = {
  field: { ...commonStyles.formField, marginBottom: '16px' },
  additionPicker: { ...commonStyles.additionSelectorContainer, marginTop: '16px' },
  radioInnerOn: { backgroundColor: 'var(--primary-color, #056666)' },
  radioInnerOff: { backgroundColor: 'transparent' },
  inputLocked: { backgroundColor: 'var(--saa-surface-muted, #f3f4f6)', cursor: 'not-allowed' as const },
  container: sectContainer,
  header: sectHeader,
  title: sectTitle,
  streetCityRowMt8: { marginTop: '8px' },
  streetCityRowMt10: { marginTop: '10px' },
};

export const premiumBreakdownStyles: Record<string, React.CSSProperties> = {
  loadingColumn: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '100px', gap: '12px' },
  loadingSpinner: { width: '40px', height: '40px', border: '4px solid rgba(0, 0, 0, 0.1)', borderTop: '4px solid var(--primary-color, #056666)', borderRadius: '50%', animation: 'spin 0.8s linear infinite' },
  loadingText: { fontSize: 'var(--saa-fs-base, 14px)', color: 'var(--saa-text-muted, #6b7280)' },
  row: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 'var(--saa-fs-base, 14px)' },
  rowLabel: { display: 'inline-flex', alignItems: 'center', gap: '10px', color: 'var(--primary-color, #056666)' },
  rowLabelText: { color: 'var(--saa-text-body, #374151)' },
  taxFootnote: { fontSize: 'var(--saa-fs-2xs, 11px)', color: 'var(--saa-text-faint, #9ca3af)', textAlign: 'left', lineHeight: 1.5 },
  card: { display: 'flex', flexDirection: 'column', gap: '8px', padding: '12px 4px 0', alignItems: 'flex-start' },
  totalBlock: { display: 'flex', flexDirection: 'column', alignItems: 'flex-start' },
  primaryAmount: { fontSize: 'var(--saa-fs-3xl, 32px)', fontWeight: 700, color: 'var(--primary-color, #056666)', lineHeight: 1.1 },
  perMonth: { fontSize: 'var(--saa-fs-sm, 13px)', color: 'var(--saa-text-muted, #6b7280)', marginTop: '6px' },
  firstPaymentBlock: { marginTop: '14px', display: 'flex', flexDirection: 'column', alignItems: 'flex-start' },
  firstPaymentAmount: { fontSize: 'var(--saa-fs-lg, 18px)', fontWeight: 600, color: 'var(--saa-text-muted, #6b7280)', lineHeight: 1.1 },
  firstPaymentLabel: { fontSize: 'var(--saa-fs-xs, 12px)', color: 'var(--saa-text-faint, #9ca3af)', marginTop: '4px' },
  policyCostNote: { fontSize: 'var(--saa-fs-2xs, 11px)', color: 'var(--saa-text-faint, #9ca3af)', marginTop: '4px' },
  breakdownBody: { marginTop: '4px', paddingTop: '10px', paddingBottom: '14px', borderTop: '1px solid var(--saa-surface-muted, #f3f4f6)', display: 'flex', flexDirection: 'column', gap: '16px', alignSelf: 'stretch' },
  group: { display: 'flex', flexDirection: 'column', gap: '10px' },
};

// Two-column layout for screens that gain the right-hand HelpCard sidebar — same
// primitives as the payment/acceptance/confirmation sidebars (flex row + 360px aside).
export const helpSidebarLayoutStyles: Record<string, React.CSSProperties> = {
  wrapper: flowContentWrapper,
  main: flowMainContent,
  aside: commonStyles.summarySidebar,
};

export const appStyles: Record<string, React.CSSProperties> = {
  configErrorBox: { padding: '40px', textAlign: 'center' as const },
  configErrorTitle: { color: 'var(--saa-danger, #d32f2f)' },
  configErrorMessage: { color: 'var(--saa-text-muted, #6b7280)' },
};
